/**
 * Schema Page
 * Manage JSON-LD structured data injection for blog posts and site pages.
 * Five tabs: Entity Settings | Signal Architect | Page Mapping | FAQ Review | Post Schema Status
 */

import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  Code,
  Building2,
  FileText,
  Globe,
  HelpCircle,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Loader2,
  RefreshCw,
  RotateCcw,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  BarChart2,
  Zap,
  Copy,
  Check,
} from 'lucide-react';
import CodeMirror from '@uiw/react-codemirror';
import { css as cssLang } from '@codemirror/lang-css';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { useAuth } from '../../contexts/AuthContext';
import {
  schemaApi,
  type SchemaGlobalSettings,
  type SchemaOrganization,
  type SchemaAuthor,
  type SchemaProduct,
  type SchemaOffer,
  type PostSchemaRow,
  type PageSchemaRow,
  type CmsPageOption,
  type PageMappingEntry,
  type SubPageMappingEntry,
  type PricingMappingEntry,
  type GlossaryMappingEntry,
  type GlossaryTermMappingEntry,
  type CmsAuthor,
  type PageHeadingAnalysis,
  type ContactPoint,
  type OpeningHoursSpec,
} from '../../lib/dashboard-api';
import { FaqReviewPanel } from '../../components/schema/FaqReviewPanel';
import { Section, Field } from '../../components/schema/SchemaFormControls';
import { normalizeSubdivision } from '../../lib/subdivision-codes';
import { COUNTRY_DIAL_CODES, flagEmoji, parsePhone } from '../../lib/country-dial-codes';
import type { CountryDialCode } from '../../lib/country-dial-codes';

// ─── Shared style constants ─────────────────────────────────────────────────

const inputCls = 'w-full px-3 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#ffdc18]/50';
const hintCls  = 'mt-1 text-xs text-white/60';
const btnPrimary = 'flex items-center gap-2 px-4 py-2 bg-[#ffdc18] text-gray-900 font-semibold rounded-lg hover:bg-[#ffdc18]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm';

// Canonical VizzEx Signal Architecture footer attribution snippet.
// Per Specifications/CLIENT-VIZZEX-BRAND-LINK-AND-FOOTER-GUIDE.md — do NOT
// change the statement text, link href, or anchor label.
const VIZZEX_ATTRIBUTION_HTML = `<!-- VizzEx Signal Architecture Verification Lock -->
<div id="vizz-attribution-gate">
  <p>
    This site\u2019s signal clarity and AI compliance is governed by VizzEx Signal Architecture:
    <a href="https://vizzex.ai/standards/signal-architecture-v1.1/"
       itemprop="isBasedOn"
       target="_blank"
       rel="nofollow">VizzEx Signal Architecture Protocol v1.1</a>
  </p>
</div>`;

// ─── Types ─────────────────────────────────────────────────────────────────

type SchemaTab = 'settings' | 'signals' | 'faqs' | 'faqcss' | 'pages' | 'posts' | 'injector';

const ORG_TYPES: Array<{ value: string; label: string }> = [
  { value: 'Organization',            label: 'Default (Organization)' },
  { value: 'ProfessionalService',     label: 'Professional Service' },
  { value: 'Consultant',              label: 'Consultant' },
  { value: 'LocalBusiness',           label: 'Local Business' },
  { value: 'EducationalOrganization', label: 'Educational Organization' },
  { value: 'GovernmentOrganization',  label: 'Government Organization' },
  { value: 'NGO',                     label: 'NGO / Non-Profit' },
  { value: 'SportsOrganization',      label: 'Sports Organization' },
  { value: 'MedicalOrganization',     label: 'Medical Organization' },
];

const PRODUCT_SCHEMA_TYPES: Array<{ value: string; label: string }> = [
  { value: '',                   label: '— Select type —' },
  { value: 'SoftwareApplication', label: 'Software Application — SaaS, apps, platforms, digital tools' },
  { value: 'ProfessionalService', label: 'Professional Service — consulting, coaching, managed services' },
  { value: 'Product',             label: 'Physical Product — tangible goods, merchandise' },
  { value: 'Course',              label: 'Course / Training — online courses, certifications, programs' },
];

const PRODUCT_SCHEMA_TYPE_LABELS: Record<string, string> = {
  SoftwareApplication: 'Software Application',
  ProfessionalService: 'Professional Service',
  Product:             'Physical Product',
  Course:              'Course / Training',
};

const CURRENCIES: Array<{ code: string; label: string }> = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'MXN', label: 'MXN — Mexican Peso' },
  { code: 'BRL', label: 'BRL — Brazilian Real' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'INR', label: 'INR — Indian Rupee' },
];

const COURSE_MODES: Array<{ value: string; label: string }> = [
  { value: 'Online',  label: 'Online' },
  { value: 'Blended', label: 'Blended' },
  { value: 'Onsite',  label: 'Onsite / In-Person' },
];

const OFFER_TYPES: Array<{ value: string; label: string }> = [
  { value: 'Service',         label: 'Service' },
  { value: 'Consultation',    label: 'Consultation' },
  { value: 'Course',          label: 'Course' },
  { value: 'Download',        label: 'Download' },
  { value: 'SoftwareLicense', label: 'Software License' },
  { value: 'Promotion',       label: 'Promotion' },
];

const BILLING_PERIODS: Array<{ value: string; label: string }> = [
  { value: 'one_time', label: 'One-time payment' },
  { value: 'monthly',  label: 'Monthly' },
  { value: 'annual',   label: 'Annual' },
  { value: 'free',     label: 'Free' },
];

const AVAILABILITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'InStock',           label: 'In Stock' },
  { value: 'OutOfStock',        label: 'Out of Stock' },
  { value: 'LimitedAvailability', label: 'Limited Availability' },
  { value: 'PreOrder',          label: 'Pre-Order' },
  { value: 'Discontinued',      label: 'Discontinued' },
  { value: 'SoldOut',           label: 'Sold Out' },
];

const OFFER_AUDIENCES: Array<{ value: string; label: string }> = [
  { value: '',         label: 'Not specified' },
  { value: 'business', label: 'Business (B2B)' },
  { value: 'consumer', label: 'Consumer (B2C)' },
  { value: 'both',     label: 'Both' },
];

const APPLIES_TO_SEARCH_THRESHOLD = 15;

const PAGE_TYPES: Array<{
  key: string;
  label: string;
  description: string;
  hasSubtype: boolean;
  hasContactPoints: boolean;
}> = [
  { key: 'homepage',  label: 'Homepage',  description: 'Your root domain page (/)',    hasSubtype: false, hasContactPoints: false },
  { key: 'about',     label: 'About Us',  description: 'Company about / story page',  hasSubtype: false, hasContactPoints: false },
  { key: 'contact',   label: 'Contact',   description: 'Contact / get in touch page', hasSubtype: false, hasContactPoints: true  },
  { key: 'services',  label: 'Services',  description: 'Services overview page',      hasSubtype: true,  hasContactPoints: false },
  { key: 'products',  label: 'Products',  description: 'Products overview page',      hasSubtype: true,  hasContactPoints: false },
];

// Matches backend slugify() — used to compute linked_entity_id from product name
function slugify(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'entity';
}

// Schema types that represent services (shown under "services" listing sub-pages)
const SERVICE_SCHEMA_TYPES = new Set(['Service', 'ProfessionalService', 'LocalBusiness', 'Course']);

function isServiceEntity(p: SchemaProduct): boolean {
  return SERVICE_SCHEMA_TYPES.has(p.schemaType);
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function statusBadge(status: string) {
  const map: Record<string, { label: string; cls: string }> = {
    injected: { label: 'Injected',          cls: 'bg-green-500/20 text-green-400 border border-green-500/30' },
    pending:  { label: 'Pending',           cls: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' },
    failed:   { label: 'Injection failed',  cls: 'bg-red-500/20 text-red-400 border border-red-500/30' },
    disabled: { label: 'Disabled',          cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' },
    skipped:  { label: 'Skipped',           cls: 'bg-blue-500/20 text-blue-400 border border-blue-500/30' },
  };
  const s = map[status] ?? { label: status, cls: 'bg-gray-500/20 text-gray-400 border border-gray-500/30' };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'injected') return <CheckCircle2 className="w-4 h-4 text-green-400" />;
  if (status === 'failed')   return <XCircle      className="w-4 h-4 text-red-400" />;
  if (status === 'pending')  return <Clock        className="w-4 h-4 text-yellow-400" />;
  return <AlertTriangle className="w-4 h-4 text-gray-400" />;
}

// ─── PageSearchCombobox ────────────────────────────────────────────────────

interface PageSearchComboboxProps {
  pages: CmsPageOption[];
  value: string | null;
  onChange: (page: CmsPageOption | null) => void;
  placeholder?: string;
}

function PageSearchCombobox({ pages, value, onChange, placeholder = 'Type to search pages…' }: PageSearchComboboxProps) {
  const [query, setQuery]       = useState('');
  const [open, setOpen]         = useState(false);
  const containerRef            = useRef<HTMLDivElement>(null);
  const inputRef                = useRef<HTMLInputElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  const selected = value ? pages.find(p => p.id === value) ?? null : null;

  const filtered = query.trim()
    ? pages.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.slug.toLowerCase().includes(query.toLowerCase()) ||
        p.url.toLowerCase().includes(query.toLowerCase())
      )
    : pages;

  // Calculate fixed-position coords each time the dropdown opens so it escapes
  // any overflow:hidden ancestor (e.g. Services/Products card containers).
  // Flips upward automatically when there isn't enough space below.
  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const dropdownHeight = Math.min(320, window.innerHeight * 0.4);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const openUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  }, [open]);

  // Close on outside click — must check both the trigger and the portal node
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = containerRef.current?.contains(target);
      const inPortal  = (document.getElementById('page-search-portal'))?.contains(target);
      if (!inTrigger && !inPortal) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on scroll / resize so the dropdown doesn't float away
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const handleSelect = (page: CmsPageOption) => {
    onChange(page);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setQuery('');
  };

  const dropdown = open ? createPortal(
    <div
      id="page-search-portal"
      style={dropdownStyle}
      className="max-h-80 overflow-y-auto bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-lg shadow-xl"
    >
      {!query.trim() ? (
        <p className="px-3 py-3 text-sm text-white/40 italic">
          Type to search all {pages.length} page{pages.length !== 1 ? 's' : ''}…
        </p>
      ) : filtered.length === 0 ? (
        <p className="px-3 py-2 text-sm text-white/40">No pages match &ldquo;{query}&rdquo;</p>
      ) : (
        <>
          {filtered.map(page => (
            <button
              key={page.id}
              onMouseDown={e => { e.preventDefault(); handleSelect(page); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--bg-tertiary)] transition-colors ${
                page.id === value ? 'bg-[#ffdc18]/10 text-[#ffdc18]' : 'text-white'
              }`}
            >
              <span className="font-medium">{page.name}</span>
              <span className="ml-2 text-xs text-white/40">{page.slug || page.url}</span>
            </button>
          ))}
          {filtered.length > 1 && (
            <p className="px-3 py-1.5 text-xs text-white/25 border-t border-white/5">
              {filtered.length} result{filtered.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div ref={containerRef} className="relative">
      <div
        className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-tertiary)] border border-[var(--bg-tertiary)] rounded-lg cursor-text"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        <Search className="w-4 h-4 text-white/40 flex-shrink-0" />
        {!open && selected ? (
          <span className="flex-1 text-sm truncate text-white">{selected.name}</span>
        ) : (
          <input
            ref={inputRef}
            value={open ? query : ''}
            placeholder={selected ? selected.name : placeholder}
            className="flex-1 bg-transparent text-sm outline-none text-white placeholder:text-white/40"
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === 'Escape') { setOpen(false); setQuery(''); }
            }}
          />
        )}
        {selected && (
          <button onClick={handleClear} className="p-0.5 rounded hover:bg-white/10">
            <X className="w-3 h-3 text-white/40" />
          </button>
        )}
      </div>
      {dropdown}
    </div>
  );
}

// ─── CmsPageUrlPicker ───────────────────────────────────────────────────────
// Generic — no CMS-specific types. Works with any {name, url} list.
// Stores the page URL as the value; falls back to manual text entry when no
// site pages are available or when the user needs to enter an external URL.

interface SitePageEntry { name: string; url: string; }

interface CmsPageUrlPickerProps {
  pages: SitePageEntry[];
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
  required?: boolean;
}

function CmsPageUrlPicker({
  pages,
  value,
  onChange,
  placeholder = 'Select a page…',
  required,
}: CmsPageUrlPickerProps) {
  const [query,      setQuery]      = useState('');
  const [open,       setOpen]       = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const containerRef                = useRef<HTMLDivElement>(null);
  const inputRef                    = useRef<HTMLInputElement>(null);

  // Match on URL — CMS-agnostic, no ID dependency
  const selected = pages.find(p => p.url === value) ?? null;

  // If we have pages but the stored value is a URL that doesn't match any
  // known page, stay in the picker but show the raw URL as hint
  const hasPages = pages.length > 0;

  const filtered = query.trim()
    ? pages.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.url.toLowerCase().includes(query.toLowerCase())
      )
    : pages;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (page: SitePageEntry) => {
    onChange(page.url);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
  };

  // Manual fallback — just a plain URL input
  if (!hasPages || manualMode) {
    return (
      <div className="space-y-1">
        <input
          value={value}
          onChange={e => onChange(e.target.value)}
          className={inputCls}
          placeholder="https://yoursite.com/page"
          required={required}
        />
        {hasPages && (
          <button
            type="button"
            onClick={() => setManualMode(false)}
            className="text-xs text-[#ffdc18]/70 hover:text-[#ffdc18] underline"
          >
            ← Pick from site pages
          </button>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative space-y-1">
      <div
        className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg cursor-text focus-within:border-[#ffdc18]/50 transition-colors"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        <Search className="w-4 h-4 text-white/40 flex-shrink-0" />
        {!open && selected ? (
          <span className="flex-1 text-sm truncate text-white">{selected.name}</span>
        ) : (
          <input
            ref={inputRef}
            value={open ? query : (value && !selected ? value : '')}
            onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
            onFocus={() => { setQuery(''); setOpen(true); }}
            placeholder={!value ? placeholder : ''}
            className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 outline-none min-w-0"
          />
        )}
        {value && !open && (
          <button type="button" onClick={handleClear}
            className="text-white/30 hover:text-white/70 transition-colors flex-shrink-0">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Hint if value is a non-matching URL */}
      {value && !selected && !open && (
        <p className="text-xs text-amber-400/70">URL not found in site pages — showing as entered.</p>
      )}

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--bg-secondary)] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-xs text-white/40 text-center py-4">No pages match "{query}"</p>
            ) : (
              filtered.map(p => (
                <button
                  key={p.url}
                  type="button"
                  onClick={() => handleSelect(p)}
                  className={`w-full flex flex-col px-3 py-2 text-sm hover:bg-white/5 transition-colors text-left ${
                    p.url === value ? 'bg-white/10' : ''
                  }`}
                >
                  <span className="text-white/90 font-medium truncate">{p.name}</span>
                  <span className="text-white/35 text-xs truncate">{p.url}</span>
                </button>
              ))
            )}
          </div>
          <div className="border-t border-white/10 px-3 py-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setManualMode(true); }}
              className="text-xs text-white/40 hover:text-white/70 transition-colors"
            >
              Enter URL manually instead →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export default function SchemaPage() {
  const { currentSite } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const siteId = currentSite?.id;

  const tabParam = searchParams.get('tab') as SchemaTab | null;
  const activeTab: SchemaTab = tabParam && ['settings', 'signals', 'faqs', 'faqcss', 'pages', 'posts', 'injector'].includes(tabParam)
    ? tabParam
    : 'settings';

  const setTab = (tab: SchemaTab) => setSearchParams({ tab });

  if (!siteId) {
    return (
      <div className="flex items-center justify-center h-64 text-white/60">
        No site selected.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#ffdc18]/10 flex items-center justify-center">
          <Code className="w-5 h-5 text-[#ffdc18]" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Semantic Schema</h1>
          <p className="text-sm text-white/60">Manage semantic schema (JSON-LD) for your blog posts and site pages</p>
        </div>
        <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-[#ffdc18]/20 text-[#ffdc18]">PRO</span>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 border-b border-[var(--bg-tertiary)]">
        {([
          { key: 'settings',  label: 'Entity Settings',    icon: Building2  },
          { key: 'pages',     label: 'Page Mapping',        icon: Globe      },
          { key: 'signals',   label: 'Signal Architect',   icon: Zap        },
          { key: 'faqs',      label: 'FAQ Review',          icon: HelpCircle },
          { key: 'faqcss',    label: 'FAQ Section CSS',     icon: Code       },
          { key: 'posts',     label: 'Post Schema Status',  icon: FileText   },
        ] as const).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === key
                ? 'border-[#ffdc18] text-[#ffdc18]'
                : 'border-transparent text-white/60 hover:text-white'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'settings'  && <SettingsTab          siteId={siteId} />}
      {activeTab === 'signals'   && <SignalArchitectTab   siteId={siteId} />}
      {activeTab === 'faqs'      && <FaqsTab              siteId={siteId} />}
      {activeTab === 'faqcss'    && <FaqCssTab            siteId={siteId} />}
      {activeTab === 'pages'     && <PagesTab             siteId={siteId} />}
      {activeTab === 'posts'     && <PostsTab             siteId={siteId} />}
    </div>
  );
}

// ─── Offer Modal ────────────────────────────────────────────────────────────

interface OfferModalProps {
  open: boolean;
  draft: SchemaOffer;
  isEditing: boolean;
  products: SchemaProduct[];
  cmsPages: SitePageEntry[];
  pricingPages: PricingMappingEntry[];
  /** All mapped action pages (standard + sub-pages + pricing) for the "Where can someone act on this offer?" picker */
  allActionPages: Array<{ label: string; url: string }>;
  appliesToSearch: string;
  onSearchChange: (v: string) => void;
  onDraftChange: (patch: Partial<SchemaOffer>) => void;
  onToggleAppliesTo: (name: string) => void;
  onSave: () => void;
  onClose: () => void;
}

function OfferModal({
  open, draft, isEditing, products, allActionPages, appliesToSearch,
  onSearchChange, onDraftChange, onToggleAppliesTo, onSave, onClose,
}: OfferModalProps) {
  if (!open) return null;

  const [showCustomUrl, setShowCustomUrl] = useState(
    !!draft.url && draft.url !== '*' && !allActionPages.some(p => p.url === draft.url)
  );

  const filteredProducts = appliesToSearch.trim()
    ? products.filter(p => p.name.toLowerCase().includes(appliesToSearch.toLowerCase()))
    : products;

  const canSave = draft.name.trim().length > 0 && draft.url.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl bg-[var(--bg-secondary)] border border-[var(--bg-tertiary)] rounded-xl shadow-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bg-tertiary)]">
          <h3 className="font-semibold text-base">{isEditing ? 'Edit Offer' : 'Add Offer'}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          <Field label="Offer Name *">
            <input
              value={draft.name}
              onChange={e => onDraftChange({ name: e.target.value })}
              className={inputCls}
              placeholder="AI Visibility Mastery Program"
              autoFocus
            />
          </Field>

          <Field label="Where can someone act on this offer? *">
            {showCustomUrl ? (
              <div className="space-y-2">
                <input
                  type="url"
                  value={draft.url ?? ''}
                  onChange={e => onDraftChange({ url: e.target.value })}
                  className={inputCls}
                  placeholder="https://yoursite.com/page"
                  autoFocus
                />
                <button type="button" onClick={() => { setShowCustomUrl(false); onDraftChange({ url: '' }); }}
                  className="text-xs text-white/40 hover:text-white transition-colors">
                  ← Back to page list
                </button>
              </div>
            ) : (
              <select
                value={draft.url ?? ''}
                onChange={e => {
                  const val = e.target.value;
                  if (val === '__custom__') { setShowCustomUrl(true); onDraftChange({ url: '' }); }
                  else onDraftChange({ url: val });
                }}
                className={`${inputCls} cursor-pointer`}
                style={{ colorScheme: 'dark' }}
              >
                <option value="">— select a page —</option>
                <option value="*">Site-wide * (appears on all matching pages)</option>
                {allActionPages.map((pg, idx) => (
                  <option key={idx} value={pg.url}>{pg.label}</option>
                ))}
                <option value="__custom__">Enter a custom URL…</option>
              </select>
            )}
            <p className={hintCls}>
              Select the page where someone can purchase or learn more about this offer — this could be a pricing page, a service page, or a contact page. If the target page isn&apos;t listed, use "Enter a custom URL" or select Site-wide.
            </p>
          </Field>

          <Field label="Checkout / Cart URL (optional)">
            <input
              type="url"
              value={draft.checkoutUrl ?? ''}
              onChange={e => onDraftChange({ checkoutUrl: e.target.value || undefined })}
              className={inputCls}
              placeholder="https://yoursite.com/checkout/"
            />
            <p className={hintCls}>
              The page that <strong>actually initiates the purchase</strong> (cart, checkout, or payment page). If set, this becomes the <code className="bg-white/10 px-1 rounded">BuyAction</code> target — must be a different URL from the page above. If left blank, no <code className="bg-white/10 px-1 rounded">BuyAction</code> is generated.
            </p>
          </Field>

          <Field label="Description">
            <textarea
              value={draft.description ?? ''}
              onChange={e => onDraftChange({ description: e.target.value || undefined })}
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder="Briefly describe this offer..."
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Offer Type">
              <select
                value={draft.offerType ?? ''}
                onChange={e => onDraftChange({ offerType: e.target.value || undefined })}
                className={`${inputCls} cursor-pointer`}
                style={{ colorScheme: 'dark' }}
              >
                <option value="">Select type...</option>
                {OFFER_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>

            <Field label="Billing Period">
              <select
                value={draft.billingPeriod ?? 'one_time'}
                onChange={e => onDraftChange({ billingPeriod: e.target.value })}
                className={`${inputCls} cursor-pointer`}
                style={{ colorScheme: 'dark' }}
              >
                {BILLING_PERIODS.map(b => (
                  <option key={b.value} value={b.value}>{b.label}</option>
                ))}
              </select>
              <p className={hintCls}>
                Emits <code className="bg-white/10 px-1 rounded">UnitPriceSpecification.unitCode</code>.
                Use "One-time" for courses with a single enrollment fee.
              </p>
            </Field>
          </div>

          <Field label="Audience (optional)">
            <select
              value={draft.offerAudience ?? ''}
              onChange={e => onDraftChange({ offerAudience: (e.target.value || undefined) as SchemaOffer['offerAudience'] })}
              className={`${inputCls} cursor-pointer`}
              style={{ colorScheme: 'dark' }}
            >
              {OFFER_AUDIENCES.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
            <p className={hintCls}>Customer type — helps differentiate B2B and B2C catalogs.</p>
          </Field>

          <Field label="Price">
            <label className="flex items-center gap-2 mb-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={draft.isFree ?? false}
                onChange={e => onDraftChange({ isFree: e.target.checked, price: e.target.checked ? undefined : draft.price })}
                className="w-4 h-4 rounded accent-[#ffdc18]"
              />
              <span className="text-sm text-white/70">This offer is free</span>
            </label>
            {!draft.isFree && (
              <div className="flex gap-2">
                <input
                  value={draft.price ?? ''}
                  onChange={e => onDraftChange({ price: e.target.value })}
                  className={`${inputCls} flex-1`}
                  placeholder="99.00"
                  type="number"
                  min="0"
                  step="0.01"
                />
                <select
                  value={draft.currency ?? 'USD'}
                  onChange={e => onDraftChange({ currency: e.target.value })}
                  className="px-2 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#ffdc18]/50 cursor-pointer"
                  style={{ colorScheme: 'dark' }}
                >
                  {CURRENCIES.map(c => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </select>
              </div>
            )}
          </Field>

          <Field label="Availability">
            <select
              value={draft.availability ?? ''}
              onChange={e => onDraftChange({ availability: e.target.value || undefined })}
              className={`${inputCls} cursor-pointer`}
              style={{ colorScheme: 'dark' }}
            >
              <option value="">— Select availability —</option>
              {AVAILABILITY_OPTIONS.map(a => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Valid From">
              <input
                type="date"
                value={draft.validFrom ?? ''}
                onChange={e => onDraftChange({ validFrom: e.target.value || undefined })}
                className={`${inputCls} cursor-pointer`}
                style={{ colorScheme: 'dark' }}
              />
            </Field>
            <Field label="Valid Through">
              <input
                type="date"
                value={draft.validThrough ?? ''}
                onChange={e => onDraftChange({ validThrough: e.target.value || undefined })}
                className={`${inputCls} cursor-pointer`}
                style={{ colorScheme: 'dark' }}
              />
            </Field>
          </div>

          <Field label="Applies To">
            {products.length === 0 ? (
              <p className="text-sm text-white/40 py-1">
                No products or services defined yet — add them in the Products &amp; Services section first.
              </p>
            ) : (
              <div className="border border-white/10 rounded-lg overflow-hidden">
                {products.length > APPLIES_TO_SEARCH_THRESHOLD && (
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
                    <Search className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
                    <input
                      value={appliesToSearch}
                      onChange={e => onSearchChange(e.target.value)}
                      placeholder="Search products..."
                      className="flex-1 bg-transparent text-sm outline-none text-white placeholder:text-white/30"
                    />
                    {appliesToSearch && (
                      <button onClick={() => onSearchChange('')} className="p-0.5 rounded hover:bg-white/10">
                        <X className="w-3 h-3 text-white/40" />
                      </button>
                    )}
                  </div>
                )}
                <div className="p-3 space-y-2 max-h-44 overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <p className="text-sm text-white/40">No products match "{appliesToSearch}"</p>
                  ) : (
                    filteredProducts.map(p => (
                      <label key={p.name} className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={(draft.appliesTo ?? []).includes(p.name)}
                          onChange={() => onToggleAppliesTo(p.name)}
                          className="w-4 h-4 rounded accent-[#ffdc18] flex-shrink-0"
                        />
                        <span className="text-sm">{p.name}</span>
                        {p.schemaType && (
                          <span className="text-xs text-white/40 italic">
                            ({PRODUCT_SCHEMA_TYPE_LABELS[p.schemaType] ?? p.schemaType})
                          </span>
                        )}
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
            <p className={hintCls}>
              Select the product or service this offer is for. If the offer bundles multiple offerings, select all that apply.
            </p>
          </Field>

        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--bg-tertiary)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button onClick={onSave} disabled={!canSave} className={btnPrimary}>
            {isEditing ? 'Save Offer' : 'Add Offer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CountrySelectInput ─────────────────────────────────────────────────────

function CountrySelectInput({
  value,
  onChange,
  placeholder = 'Select country…',
}: {
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
}) {
  const [query, setQuery]       = useState('');
  const [open, setOpen]         = useState(false);
  const containerRef            = useRef<HTMLDivElement>(null);
  const inputRef                = useRef<HTMLInputElement>(null);

  // Resolve display name from code or match full-name input
  const selected = COUNTRY_DIAL_CODES.find(
    c => c.code === value.toUpperCase() || c.name.toLowerCase() === value.toLowerCase()
  ) ?? null;

  const displayValue = open ? query : (selected ? selected.name : value);

  const filtered = query.trim()
    ? COUNTRY_DIAL_CODES.filter(c =>
        c.name.toLowerCase().includes(query.toLowerCase()) ||
        c.code.toLowerCase().includes(query.toLowerCase())
      )
    : COUNTRY_DIAL_CODES;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (c: CountryDialCode) => {
    onChange(c.code);
    setQuery('');
    setOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!open) setOpen(true);
  };

  const handleFocus = () => {
    setQuery('');
    setOpen(true);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg cursor-text focus-within:border-[#ffdc18]/50 transition-colors">
        {selected && !open && (
          <span className="text-base leading-none flex-shrink-0">{flagEmoji(selected.code)}</span>
        )}
        <input
          ref={inputRef}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 outline-none min-w-0"
        />
        {selected && !open && (
          <span className="text-xs text-white/40 font-mono flex-shrink-0">{selected.code}</span>
        )}
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--bg-secondary)] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-xs text-white/40 text-center py-4">No countries found</p>
            )}
            {filtered.map(c => (
              <button
                key={c.code}
                type="button"
                onClick={() => handleSelect(c)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-white/5 transition-colors text-left ${
                  c.code === selected?.code ? 'bg-white/10' : ''
                }`}
              >
                <span className="text-base leading-none w-6 text-center">{flagEmoji(c.code)}</span>
                <span className="flex-1 truncate text-white/90">{c.name}</span>
                <span className="text-white/40 text-xs font-mono">{c.code}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CountryPhoneInput ──────────────────────────────────────────────────────

function CountryPhoneInput({
  value,
  onChange,
  placeholder = '555 000 0000',
  className = '',
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const parsed                          = parsePhone(value);
  const [country, setCountry]           = useState<CountryDialCode>(parsed.country);
  const [local, setLocal]               = useState(parsed.local);
  const [open, setOpen]                 = useState(false);
  const [search, setSearch]             = useState('');
  const containerRef                    = useRef<HTMLDivElement>(null);
  const searchRef                       = useRef<HTMLInputElement>(null);

  // Sync when external value changes (e.g. loaded from DB)
  useEffect(() => {
    const p = parsePhone(value);
    setCountry(p.country);
    setLocal(p.local);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const emit = (c: CountryDialCode, l: string) => {
    const digits = l.replace(/[^\d\s\-().+]/g, '');
    onChange(digits.trim() ? `${c.dial} ${digits.trim()}` : '');
  };

  const handleCountrySelect = (c: CountryDialCode) => {
    setCountry(c);
    setOpen(false);
    setSearch('');
    emit(c, local);
  };

  const handleLocalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setLocal(val);
    emit(country, val);
  };

  const filtered = search.trim()
    ? COUNTRY_DIAL_CODES.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dial.includes(search)
      )
    : COUNTRY_DIAL_CODES;

  return (
    <div ref={containerRef} className={`relative flex gap-2 ${className}`}>
      {/* Country selector */}
      <button
        type="button"
        onClick={() => { setOpen(o => !o); setTimeout(() => searchRef.current?.focus(), 50); }}
        className="flex items-center gap-1.5 px-3 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm whitespace-nowrap hover:border-white/20 transition-colors flex-shrink-0"
        title={`${country.name} (${country.dial})`}
      >
        <span className="text-base leading-none">{flagEmoji(country.code)}</span>
        <span className="text-white/70">{country.dial}</span>
        <ChevronDown className="w-3 h-3 text-white/40" />
      </button>

      {/* Number input */}
      <input
        type="tel"
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder}
        className="flex-1 px-3 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#ffdc18]/50 min-w-0"
      />

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 bg-[var(--bg-secondary)] border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <input
              ref={searchRef}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search country or code…"
              className="w-full px-3 py-1.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/40 focus:outline-none"
            />
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="text-xs text-white/40 text-center py-4">No countries found</p>
            )}
            {filtered.map(c => (
              <button
                key={`${c.code}-${c.dial}`}
                type="button"
                onClick={() => handleCountrySelect(c)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-white/5 transition-colors text-left ${
                  c.code === country.code ? 'bg-white/10' : ''
                }`}
              >
                <span className="text-base leading-none w-6 text-center">{flagEmoji(c.code)}</span>
                <span className="flex-1 truncate text-white/90">{c.name}</span>
                <span className="text-white/40 text-xs font-mono">{c.dial}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab: Signal Architect (DEV-SPEC-001) ──────────────────────────────────

function SignalArchitectTab({ siteId }: { siteId: number }) {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [enabled,            setEnabled]            = useState(false);
  const [freshnessEnabled,   setFreshnessEnabled]   = useState(false);
  const [authorFooter,       setAuthorFooter]       = useState(false);
  const [figureWrap,         setFigureWrap]         = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await schemaApi.getGlobalSettings(siteId);
        if (cancelled) return;
        setEnabled(settings.html5InjectionEnabled    ?? false);
        setFreshnessEnabled(settings.html5FreshnessEnabled    ?? false);
        setAuthorFooter(settings.html5AuthorFooterEnabled ?? false);
        setFigureWrap(settings.html5FigureWrapEnabled   ?? false);
      } catch {
        if (!cancelled) setMessage({ type: 'error', text: 'Failed to load settings.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await schemaApi.saveGlobalSettings(siteId, {
        html5InjectionEnabled:    enabled,
        html5FreshnessEnabled:    freshnessEnabled,
        html5AuthorFooterEnabled: authorFooter,
        html5FigureWrapEnabled:   figureWrap,
      });
      setMessage({ type: 'success', text: 'Settings saved.' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-white/60">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Master toggle */}
      <div className="flex items-center justify-between p-4 rounded-lg bg-white/5 border border-white/10">
        <div>
          <p className="text-sm font-medium text-white">Signal Architecture</p>
          <p className="text-xs text-white/60 mt-0.5 leading-relaxed">
            Wraps post content in semantic HTML5 scaffolding — <code className="text-white/80">&lt;article&gt;</code>,{' '}
            <code className="text-white/80">&lt;section aria-labelledby&gt;</code>, and deterministic heading IDs —
            so AI crawlers can reliably parse and cite your content.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setEnabled(v => !v)}
          className={`ml-6 flex-shrink-0 relative w-10 h-5 rounded-full transition-colors ${enabled ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
          aria-label={enabled ? 'Disable Signal Architecture' : 'Enable Signal Architecture'}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>

      {/* Sub-feature toggles — only shown when master is on */}
      {enabled && (
        <div className="space-y-3 pl-2 border-l-2 border-white/10">

          {/* Freshness Signals */}
          <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
            <div>
              <p className="text-sm font-medium text-white">
                Freshness Signals{' '}
                <code className="text-xs font-normal text-white/60">&lt;footer&gt; + &lt;time&gt;</code>
              </p>
              <p className="text-xs text-white/60 mt-0.5 leading-relaxed">
                Injects a <code className="text-white/80">&lt;footer&gt;</code> block just before{' '}
                <code className="text-white/80">&lt;/article&gt;</code> with{' '}
                <code className="text-white/80">&lt;time itemprop="datePublished"&gt;</code> and{' '}
                <code className="text-white/80">&lt;time itemprop="dateModified"&gt;</code> tags
                using the post&rsquo;s published and last-modified dates. Signals content freshness to AI crawlers.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFreshnessEnabled(v => !v)}
              className={`flex-shrink-0 relative w-10 h-5 rounded-full transition-colors ${freshnessEnabled ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
              aria-label={freshnessEnabled ? 'Disable freshness signals' : 'Enable freshness signals'}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${freshnessEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Author E-E-A-T Footer */}
          <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
            <div>
              <p className="text-sm font-medium text-white">
                Author E-E-A-T Footer{' '}
                <code className="text-xs font-normal text-white/60">&lt;footer&gt;</code>
              </p>
              <p className="text-xs text-white/60 mt-0.5 leading-relaxed">
                Appends a <code className="text-white/80">&lt;footer itemprop="author"&gt;</code> inside the article
                with author name, job title, and bio from your Author entities. Requires the author&rsquo;s HubSpot
                display name to be added to the <strong className="text-white/80">Author Mapping</strong> field
                on each Author entity in Schema Setup.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAuthorFooter(v => !v)}
              className={`flex-shrink-0 relative w-10 h-5 rounded-full transition-colors ${authorFooter ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
              aria-label={authorFooter ? 'Disable author footer' : 'Enable author footer'}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${authorFooter ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Auto Figure Wrapping */}
          <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/10">
            <div>
              <p className="text-sm font-medium text-white">
                Auto Figure Wrapping{' '}
                <code className="text-xs font-normal text-white/60">&lt;figure&gt; + &lt;figcaption&gt;</code>
              </p>
              <p className="text-xs text-white/60 mt-0.5 leading-relaxed">
                Wraps bare <code className="text-white/80">&lt;img&gt;</code> tags (not already inside a{' '}
                <code className="text-white/80">&lt;figure&gt;</code>) with{' '}
                <code className="text-white/80">&lt;figure class="vizzex-figure-sig"&gt;</code> and a{' '}
                <code className="text-white/80">&lt;figcaption&gt;</code> derived from the image&rsquo;s alt text.
                Images without alt text are skipped. Improves multimodal AI context.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFigureWrap(v => !v)}
              className={`flex-shrink-0 relative w-10 h-5 rounded-full transition-colors ${figureWrap ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
              aria-label={figureWrap ? 'Disable auto figure wrapping' : 'Enable auto figure wrapping'}
            >
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${figureWrap ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

        </div>
      )}

      {/* Feedback */}
      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'
        }`}>
          {message.type === 'success'
            ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            : <XCircle className="w-4 h-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#ffdc18] text-black text-sm font-semibold hover:bg-[#ffdc18]/90 disabled:opacity-50 transition-colors"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        {saving ? 'Saving…' : 'Save Signal Architect Settings'}
      </button>
    </div>
  );
}

// ─── Tab 1: Entity Settings ────────────────────────────────────────────────

function SettingsTab({ siteId }: { siteId: number }) {
  const [loading, setLoading]         = useState(true);
  const [saving,  setSaving]          = useState<'idle' | 'saving' | 'syncing' | 'regen-posts' | 'regen-pages'>('idle');
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [message, setMessage]         = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [footerHtmlCopied, setFooterHtmlCopied] = useState(false);

  const [globalSettings, setGlobalSettings] = useState<SchemaGlobalSettings>({
    schemaOutputEnabled: false,
    enableFaqExtraction: true,
    maxFaqsPerPost: 5,
    faqInjectionEnabled: true,
    vizzexBrandLink: false,
    isVizzexHost: false,
  });

  const [org, setOrg]         = useState<SchemaOrganization>({ name: '', url: '' });
  const [logoUrlError, setLogoUrlError] = useState('');
  const [regionNormalized, setRegionNormalized] = useState<string | null>(null);
  const [authors, setAuthors] = useState<SchemaAuthor[]>([]);
  const [products, setProducts] = useState<SchemaProduct[]>([]);
  const [offers, setOffers]   = useState<SchemaOffer[]>([]);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);
  const [configHash, setConfigHash] = useState<string | null>(null);
  const [cmsNativeSchema, setCmsNativeSchema] = useState<{
    cmsType: string;
    blogs: Array<{ id: string; name: string; absoluteUrl: string | null; nativeSchemaEnabled: boolean }>;
    anyEnabled: boolean | null;
    unsupported?: boolean;
    error?: string;
    loading: boolean;
  }>({ cmsType: 'hubspot', blogs: [], anyEnabled: null, loading: true });

  // Site pages for URL pickers (product URL fields)
  const [entityCmsPages, setEntityCmsPages] = useState<SitePageEntry[]>([]);
  // Pricing pages for offer URL picker
  const [settingsPricingPages, setSettingsPricingPages] = useState<PricingMappingEntry[]>([]);
  const [settingsAllActionPages, setSettingsAllActionPages] = useState<Array<{ label: string; url: string }>>([]);

  const updateSameAs = (index: number, value: string) => {
    setOrg(o => { const next = [...(o.sameAs ?? [])]; next[index] = value; return { ...o, sameAs: next }; });
  };
  const addSameAs    = () => setOrg(o => ({ ...o, sameAs: [...(o.sameAs ?? []), ''] }));
  const removeSameAs = (index: number) => setOrg(o => ({ ...o, sameAs: (o.sameAs ?? []).filter((_, i) => i !== index) }));

  // Org knows_about helpers
  const updateKnowsAbout = (index: number, value: string) =>
    setOrg(o => { const next = [...(o.knows_about ?? [])]; next[index] = value; return { ...o, knows_about: next }; });
  const addKnowsAbout    = () => setOrg(o => ({ ...o, knows_about: [...(o.knows_about ?? []), ''] }));
  const removeKnowsAbout = (index: number) => setOrg(o => ({ ...o, knows_about: (o.knows_about ?? []).filter((_, i) => i !== index) }));
  const stripUrl = (url: string) => url.replace(/\/+$/, '');
  const validateLogoUrl = (val: string) => {
    if (!val) { setLogoUrlError(''); return; }
    setLogoUrlError(/^https?:\/\/.+\..+/.test(val) ? '' : 'Must be a valid URL starting with http:// or https://');
  };

  // Author card state
  const [expandedAuthorIdx, setExpandedAuthorIdx] = useState<number | null>(null);
  const [importingAuthors,  setImportingAuthors]  = useState(false);

  const updateAuthor = (i: number, patch: Partial<SchemaAuthor>) =>
    setAuthors(arr => arr.map((a, j) => j === i ? { ...a, ...patch } : a));

  const getAuthorArr = (a: SchemaAuthor, field: 'sameAs' | 'knowsAbout' | 'authorMapping'): string[] =>
    (a[field] as string[] | undefined) ?? [];

  const updateAuthorArr = (i: number, field: 'sameAs' | 'knowsAbout' | 'authorMapping', idx: number, val: string) =>
    setAuthors(arr => arr.map((a, j) => {
      if (j !== i) return a;
      const next = [...getAuthorArr(a, field)];
      next[idx] = val;
      return { ...a, [field]: next };
    }));

  const addAuthorArrItem = (i: number, field: 'sameAs' | 'knowsAbout' | 'authorMapping') =>
    setAuthors(arr => arr.map((a, j) => j !== i ? a : { ...a, [field]: [...getAuthorArr(a, field), ''] }));

  const removeAuthorArrItem = (i: number, field: 'sameAs' | 'knowsAbout' | 'authorMapping', idx: number) =>
    setAuthors(arr => arr.map((a, j) => j !== i ? a : { ...a, [field]: getAuthorArr(a, field).filter((_, k) => k !== idx) }));

  const handleImportAuthors = async () => {
    setImportingAuthors(true);
    try {
      const result = await schemaApi.getCmsAuthors(siteId);
      const existingNames = new Set(authors.map(a => a.name.toLowerCase().trim()));
      const newAuthors: SchemaAuthor[] = (result.authors as CmsAuthor[])
        .filter(a => !existingNames.has(a.name.toLowerCase().trim()))
        .map(a => ({
          name:          a.name,
          url:           a.profileUrl || undefined,
          image:         a.image      || undefined,
          description:   a.bio        || undefined,
          email:         a.email      || undefined,
          authorMapping: a.email ? [a.email] : [],
        }));
      if (result.authors.length === 0) {
        setMessage({ type: 'info', text: 'No authors found in your CMS. Create blog authors in HubSpot first, then import.' });
      } else if (newAuthors.length > 0) {
        setAuthors(prev => [...prev, ...newAuthors]);
        setMessage({ type: 'success', text: `Imported ${newAuthors.length} author${newAuthors.length !== 1 ? 's' : ''} from CMS.` });
      } else {
        setMessage({ type: 'success', text: 'All CMS authors are already in your list.' });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to import authors from CMS. Please try again.' });
    } finally {
      setImportingAuthors(false);
    }
  };

  const cmsImportLabel = (cmsType?: string): string | null => {
    if (cmsType === 'hubspot')   return 'Import from HubSpot';
    if (cmsType === 'wordpress') return 'Import from WordPress';
    return null;
  };

  // Product card state
  const [expandedProductIdx, setExpandedProductIdx] = useState<number | null>(null);

  const updateProduct = (i: number, patch: Partial<SchemaProduct>) =>
    setProducts(arr => arr.map((p, j) => j === i ? { ...p, ...patch } : p));

  const getProductArr = (p: SchemaProduct, field: 'features' | 'instructors'): string[] =>
    (p[field] as string[] | undefined) ?? [];

  const updateProductArr = (i: number, field: 'features', idx: number, val: string) =>
    setProducts(arr => arr.map((p, j) => {
      if (j !== i) return p;
      const next = [...getProductArr(p, field)];
      next[idx] = val;
      return { ...p, [field]: next };
    }));

  const addProductArrItem = (i: number, field: 'features') =>
    setProducts(arr => arr.map((p, j) => j !== i ? p : { ...p, [field]: [...getProductArr(p, field), ''] }));

  const removeProductArrItem = (i: number, field: 'features', idx: number) =>
    setProducts(arr => arr.map((p, j) => j !== i ? p : { ...p, [field]: getProductArr(p, field).filter((_, k) => k !== idx) }));

  const toggleInstructor = (i: number, authorName: string) =>
    setProducts(arr => arr.map((p, j) => {
      if (j !== i) return p;
      const current = p.instructors ?? [];
      const next = current.includes(authorName)
        ? current.filter(n => n !== authorName)
        : [...current, authorName];
      return { ...p, instructors: next };
    }));

  // Offer modal state
  const [offerModalOpen,  setOfferModalOpen]  = useState(false);
  const [editingOfferIdx, setEditingOfferIdx] = useState<number | null>(null);
  const [offerDraft,      setOfferDraft]      = useState<SchemaOffer>({ name: '', url: '' });
  const [appliesToSearch, setAppliesToSearch] = useState('');

  const openAddOfferModal = () => {
    setOfferDraft({ name: '', url: '', billingPeriod: 'one_time', currency: 'USD' });
    setEditingOfferIdx(null);
    setAppliesToSearch('');
    setOfferModalOpen(true);
  };

  const openEditOfferModal = (idx: number) => {
    setOfferDraft({ ...offers[idx] });
    setEditingOfferIdx(idx);
    setAppliesToSearch('');
    setOfferModalOpen(true);
  };

  const closeOfferModal = () => {
    setOfferModalOpen(false);
    setEditingOfferIdx(null);
    setOfferDraft({ name: '', url: '' });
    setAppliesToSearch('');
  };

  const saveOffer = () => {
    if (!offerDraft.name.trim() || !offerDraft.url.trim()) return;
    if (editingOfferIdx !== null) {
      setOffers(prev => prev.map((o, i) => i === editingOfferIdx ? offerDraft : o));
    } else {
      setOffers(prev => [...prev, offerDraft]);
    }
    closeOfferModal();
  };

  const deleteOffer = (idx: number) => setOffers(prev => prev.filter((_, i) => i !== idx));

  const toggleOfferAppliesTo = (productName: string) => {
    const current = offerDraft.appliesTo ?? [];
    const next = current.includes(productName)
      ? current.filter(n => n !== productName)
      : [...current, productName];
    setOfferDraft(d => ({ ...d, appliesTo: next }));
  };

  useEffect(() => {
    Promise.all([
      schemaApi.getGlobalSettings(siteId),
      schemaApi.getEntities(siteId),
    ]).then(([gs, entities]) => {
      setGlobalSettings(gs);
      if (entities.organization) setOrg(entities.organization);
      setAuthors(entities.authors ?? []);
      setProducts(entities.products ?? []);
      setOffers(entities.offers ?? []);
      setLastSyncAt(entities.lastSyncAt ?? null);
      setConfigHash(entities.configHash ?? null);
      setLastSavedAt(gs.lastSavedAt ?? null);
    }).catch(console.error).finally(() => setLoading(false));

    // Load CMS native schema status separately — non-blocking
    schemaApi.getCmsNativeSchemaStatus(siteId)
      .then(data => setCmsNativeSchema({ ...data, loading: false }))
      .catch(() => setCmsNativeSchema(s => ({ ...s, loading: false, error: 'Could not check CMS schema status' })));

    // Load site pages for URL pickers — non-blocking, uses cached list
    schemaApi.getCmsPages(siteId).then(result => {
      setEntityCmsPages(result.pages.map(p => ({ name: p.name, url: p.url })));
    }).catch(() => { /* pages unavailable — pickers fall back to manual input */ });

    // Load pricing page mappings for offer URL picker
    schemaApi.getPageMapping(siteId).then(result => {
      setSettingsPricingPages(result.pricingMappings ?? []);
      // Build a flat list of all mapped pages for the offer "action page" picker
      const actionPages: Array<{ label: string; url: string }> = [];
      // Standard pages
      const PAGE_TYPE_LABELS: Record<string, string> = {
        homepage: 'Homepage', about: 'About', contact: 'Contact',
        services: 'Services', products: 'Products',
      };
      Object.entries(result.mapping ?? {}).forEach(([key, entry]) => {
        if (entry?.cmsPageUrl) {
          actionPages.push({ label: `${PAGE_TYPE_LABELS[key] ?? key} — ${entry.cmsPageName ?? entry.cmsPageUrl}`, url: entry.cmsPageUrl });
        }
      });
      // Pricing pages
      (result.pricingMappings ?? []).forEach(pp => {
        if (pp.cmsPageUrl) actionPages.push({ label: `Pricing — ${pp.cmsPageName ?? pp.cmsPageUrl}`, url: pp.cmsPageUrl });
      });
      // Sub-pages (product/service)
      (result.subPageMappings ?? []).forEach(sp => {
        if (sp.cmsPageUrl) {
          const typeLabel = sp.pageType === 'product_page' ? 'Product page' : 'Service page';
          actionPages.push({ label: `${typeLabel} — ${sp.entityName}`, url: sp.cmsPageUrl });
        }
      });
      setSettingsAllActionPages(actionPages);
    }).catch(() => { /* pages unavailable */ });
  }, [siteId]);

  const handleToggleOutput = async (enabled: boolean) => {
    const updated = { ...globalSettings, schemaOutputEnabled: enabled };
    setGlobalSettings(updated);
    await schemaApi.saveGlobalSettings(siteId, { schemaOutputEnabled: enabled });
  };

  const entityPayload = {
    organization: {
      ...org,
      logo: org.logo ? stripUrl(org.logo) : org.logo,
      locationImage: org.locationImage?.url
        ? { ...org.locationImage, url: stripUrl(org.locationImage.url) }
        : org.locationImage,
    },
    authors: authors.map(a => ({ ...a, image: a.image ? stripUrl(a.image) : a.image })),
    products: products.map(p => ({ ...p, image: p.image ? stripUrl(p.image) : p.image })),
    offers,
    enableFaqExtraction: globalSettings.enableFaqExtraction,
    maxFaqsPerPost: globalSettings.maxFaqsPerPost,
    vizzexBrandLink: globalSettings.vizzexBrandLink ?? false,
  };

  /** Pull the latest post schemas from BKA and inject them (no new analysis needed). */
  // Ref to clear the polling interval on unmount or early completion.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  // Clean up on unmount so we don't update state on an unmounted component.
  useEffect(() => () => { stopPolling(); }, []);

  const handleRegeneratePosts = async () => {
    setSaving('regen-posts');
    setMessage(null);
    stopPolling();
    try {
      const result = await schemaApi.fetchAndInjectPosts(siteId);

      if (result.skipped && result.reason === 'schema_disabled') {
        setMessage({ type: 'error', text: 'Schema output is disabled. Enable the "Schema Output" toggle above before regenerating.' });
        setSaving('idle');
        return;
      }

      // 202 async path — start polling for job completion.
      if (result.jobId) {
        const jobId     = result.jobId;
        const startedAt = Date.now();
        const TIMEOUT   = 10 * 60 * 1000; // 10 minutes

        setMessage({ type: 'info', text: 'Processing posts — this may take a moment…' });

        pollIntervalRef.current = setInterval(async () => {
          try {
            if (Date.now() - startedAt > TIMEOUT) {
              stopPolling();
              setSaving('idle');
              setMessage({ type: 'error', text: 'Regeneration is taking longer than expected. Check the Post Schema tab for current status.' });
              return;
            }

            const job = await schemaApi.getJobStatus(jobId, siteId);

            if (job.status === 'done') {
              stopPolling();
              setSaving('idle');
              const r = job.result;
              const faqNote    = (r?.faqsStored ?? 0) > 0 ? ` ${r!.faqsStored} FAQs synced.` : '';
              const skipNote   = (r?.skipped ?? 0) > 0 ? ` ${r!.skipped} already up to date.` : '';
              const injected   = r?.injected ?? 0;
              const total      = injected + (r?.skipped ?? 0);
              const summary    = injected > 0
                ? `${injected} updated, ${r?.failed ?? 0} failed.`
                : `All ${total} post schemas present and up to date.`;

              // regenerateAllResult reflects BKA's own success/failure for the rebuild step —
              // a later GET returning data is not proof every post was regenerated AND saved.
              const regen = r?.regenerateAllResult;
              if (regen && regen.success === false) {
                const persistFailed = regen.persistFailedPosts ?? [];
                const buildFailed   = regen.failedPosts ?? [];
                const savedCount    = regen.schemasSaved ?? 0;
                const totalCount    = regen.totalPosts ?? 0;
                const parts: string[] = [];
                if (persistFailed.length > 0) {
                  parts.push(`not saved: ${persistFailed.join(', ')}`);
                }
                if (buildFailed.length > 0) {
                  parts.push(`build failed: ${buildFailed.join(', ')}`);
                }
                const detail = parts.length > 0 ? ` (${parts.join('; ')})` : '';
                setMessage({
                  type: 'warning',
                  text: `Post schema regeneration incomplete: ${savedCount} of ${totalCount} posts saved${detail}. These posts were NOT regenerated — click "Regenerate Post Schema" again to retry them.`,
                });
                return;
              }

              setMessage({ type: 'success', text: `Post schema: ${summary}${skipNote ? '' : ''}${faqNote}` });
            } else if (job.status === 'failed') {
              stopPolling();
              setSaving('idle');
              setMessage({ type: 'error', text: `Regeneration failed: ${job.error ?? 'Unknown error'}` });
            }
            // status === 'processing' → keep polling
          } catch {
            // Network hiccup — keep polling, don't abort
          }
        }, 2000);
        return; // setSaving('idle') handled by poll callbacks above
      }

      // Synchronous path (should only be skipped cases going forward, but handle defensively)
      const faqNote = (result.faqsStored ?? 0) > 0 ? ` ${result.faqsStored} FAQs synced.` : '';
      setMessage({ type: 'success', text: `Post schema regenerated: ${result.fetched ?? 0} fetched, ${result.injected ?? 0} injected.${faqNote}` });
    } catch {
      setMessage({ type: 'error', text: 'Failed to start post schema regeneration. Please try again.' });
    } finally {
      // Only reset spinner here if we did NOT start async polling (polling manages it itself).
      if (pollIntervalRef.current === null) setSaving('idle');
    }
  };

  /** Re-submit mapped pages to BKA and inject the resulting page schemas. */
  const handleRegeneratePages = async () => {
    setSaving('regen-pages');
    setMessage(null);
    stopPolling();
    try {
      const result = await schemaApi.submitPagesToBka(siteId);

      if (result.jobId) {
        const jobId     = result.jobId;
        const startedAt = Date.now();
        const TIMEOUT   = 5 * 60 * 1000; // 5 minutes

        setMessage({ type: 'info', text: 'Submitting to schema server and injecting pages — this may take a moment…' });

        pollIntervalRef.current = setInterval(async () => {
          try {
            if (Date.now() - startedAt > TIMEOUT) {
              stopPolling();
              setSaving('idle');
              setMessage({ type: 'error', text: 'Page schema submission is taking longer than expected. Check page statuses and try again.' });
              return;
            }

            const job = await schemaApi.getJobStatus(jobId, siteId);

            if (job.status === 'done') {
              stopPolling();
              setSaving('idle');
              const r = job.result;
              const injectionNote = !r?.schemaOutputEnabled
                ? ' Schema Output is disabled — turn it on in Entity Settings to inject schemas into your pages.'
                : '';
              const warningLines = (r?.warnings?.length ?? 0) > 0
                ? '\nWarnings:\n' + r!.warnings!.map((w: string) => `• ${w}`).join('\n')
                : '';
              setMessage({
                type: (r?.warnings?.length ?? 0) > 0 ? 'warning' : 'success',
                text: `Page schema regenerated: ${r?.injected ?? 0} injected, ${r?.failed ?? 0} failed.${injectionNote}${warningLines}`,
              });
            } else if (job.status === 'failed') {
              stopPolling();
              setSaving('idle');
              setMessage({ type: 'error', text: `Page schema regeneration failed: ${job.error ?? 'Unknown error'}` });
            }
          } catch {
            // Network hiccup — keep polling
          }
        }, 3000);
        return;
      }

      // Synchronous fallback (should not occur with new backend, but handle defensively)
      setMessage({ type: 'success', text: `Page schema regenerated: ${result.injected ?? 0} injected, ${result.failed ?? 0} failed.` });
    } catch {
      setMessage({ type: 'error', text: 'Failed to regenerate page schema. Please try again.' });
    } finally {
      if (pollIntervalRef.current === null) setSaving('idle');
    }
  };

  /** Save entities to database AND push to BKA schema generator. */
  const handleSync = async () => {
    // Pre-flight validation — block on fields that cause hard BKA errors.
    // Note: authors are not required for valid schema, but each author that IS
    // defined must have a name and at least one author mapping.
    const syncErrors: string[] = [];
    authors.forEach((a, i) => {
      const label = a.name?.trim() ? `Author "${a.name.trim()}"` : `Author #${i + 1}`;
      if (!a.name?.trim()) syncErrors.push(`${label}: name is required.`);
      const mappings = (a.authorMapping ?? []).filter(m => m.trim());
      if (mappings.length === 0) syncErrors.push(`${label}: at least one Author Mapping entry is required.`);
    });
    if (syncErrors.length > 0) {
      setMessage({ type: 'error', text: `Cannot sync — please fix the following:\n• ${syncErrors.join('\n• ')}` });
      return;
    }

    setSaving('syncing');
    setMessage(null);
    try {
      const result = await schemaApi.saveAndSyncEntities(siteId, entityPayload);
      const now = new Date().toISOString();
      setLastSavedAt(now);
      setLastSyncAt(now);
      setConfigHash(result.configHash);
      const pagesNote = result.regenerationRequired?.all_pages
        ? ' Pages are being regenerated in the background — this can take a few minutes.'
        : '';
      setMessage({
        type: 'success',
        text: (result.synced ? 'Saved and synced to server.' : 'Saved — no schema changes detected.') + pagesNote,
      });
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const apiDetail = (err as any)?.response?.data?.details || (err as any)?.response?.data?.error;
      const msg = apiDetail
        ? `Failed to sync to server: ${apiDetail}`
        : 'Failed to sync to server. Please try again.';
      console.error('[handleSync] error:', err);
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving('idle');
    }
  };

  /** Save a specific entity section to the database (DB-only, no BKA sync). */
  const handleSaveSection = async (sectionLabel: string) => {
    if (saving !== 'idle' || savingSection !== null) return;
    setSavingSection(sectionLabel);
    setMessage(null);
    try {
      const result = await schemaApi.saveEntities(siteId, entityPayload);
      setLastSavedAt(new Date().toISOString());
      setConfigHash(result.configHash);
      setMessage({ type: 'success', text: `${sectionLabel} saved.` });
    } catch {
      setMessage({ type: 'error', text: `Failed to save ${sectionLabel}. Please try again.` });
    } finally {
      setSavingSection(null);
    }
  };

  const handleCopyFooterHtml = () => {
    navigator.clipboard.writeText(VIZZEX_ATTRIBUTION_HTML);
    setFooterHtmlCopied(true);
    setTimeout(() => setFooterHtmlCopied(false), 2000);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Master toggle */}
      <div className="bg-[var(--bg-secondary)] rounded-xl p-5 border border-[var(--bg-tertiary)]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Enable Schema Output</h3>
            <p className="text-sm text-white/60 mt-0.5">
              When on, JSON-LD schema is automatically injected into your posts after each analysis.
            </p>
          </div>
          <button
            onClick={() => handleToggleOutput(!globalSettings.schemaOutputEnabled)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              globalSettings.schemaOutputEnabled ? 'bg-[#ffdc18]' : 'bg-white/20'
            }`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              globalSettings.schemaOutputEnabled ? 'translate-x-6' : 'translate-x-0'
            }`} />
          </button>
        </div>
      </div>

      {/* CMS Schema Compatibility */}
      {(() => {
        const { loading, cmsType, blogs, anyEnabled, unsupported, error } = cmsNativeSchema;
        if (loading) return null;
        if (unsupported || error) return null; // Silently skip for unknown CMS types or API errors

        // Build CMS-specific instructions keyed by cmsType — add new CMS cases here
        const instructions: Record<string, { title: string; steps: string[]; settingPath: string }> = {
          hubspot: {
            title: 'HubSpot native BlogPosting schema',
            settingPath: 'Settings → Website → Blog → General tab → "Structured data"',
            steps: [
              'In HubSpot, go to Settings (gear icon) → Website → Blog.',
              'Use the domain picker at the top to select your blog domain.',
              'Under the General tab, find the "Structured data" toggle.',
              'Turn it OFF and save.',
            ],
          },
        };

        const cms = instructions[cmsType];
        if (!cms) return null;

        const isConflict = anyEnabled === true;
        const isSafe     = anyEnabled === false;

        return (
          <div className={`rounded-xl p-5 border ${isConflict ? 'border-red-500/40 bg-red-500/5' : 'border-green-500/30 bg-green-500/5'}`}>
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${isConflict ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                {isConflict
                  ? <span className="text-red-400 text-xs font-bold">!</span>
                  : <span className="text-green-400 text-xs">✓</span>}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold text-sm ${isConflict ? 'text-red-300' : 'text-green-300'}`}>
                  {isConflict
                    ? `Duplicate schema conflict — ${cms.title} is ON`
                    : `${cms.title} is OFF`}
                </h3>
                <p className="text-xs text-white/50 mt-0.5">
                  {isConflict
                    ? 'Your CMS is injecting its own schema on blog posts alongside VizzEx. This creates duplicate schema which can confuse search engines. Disable it using the steps below.'
                    : 'No duplicate schema conflict. VizzEx schema is the only schema on your blog posts.'}
                </p>

                {/* Per-blog status when there are multiple blogs */}
                {blogs.length > 1 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {blogs.map(b => (
                      <span key={b.id} className={`text-xs px-2 py-0.5 rounded-full ${b.nativeSchemaEnabled ? 'bg-red-500/20 text-red-300' : 'bg-green-500/10 text-green-400'}`}>
                        {b.name}: {b.nativeSchemaEnabled ? 'ON' : 'OFF'}
                      </span>
                    ))}
                  </div>
                )}

                {isConflict && (
                  <details className="mt-3">
                    <summary className="text-xs text-white/60 cursor-pointer hover:text-white/80 select-none">
                      How to fix — {cms.settingPath}
                    </summary>
                    <ol className="mt-2 space-y-1 list-decimal list-inside text-xs text-white/50">
                      {cms.steps.map((step, i) => (
                        <li key={i}>{step}</li>
                      ))}
                    </ol>
                  </details>
                )}

                {isSafe && (
                  <p className={hintCls}>
                    To verify: {cms.settingPath}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Organization */}
      <Section title="Organization" icon={<Building2 className="w-4 h-4" />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

          {/* Row 1: Name + Legal Name */}
          <Field label="Organization Name *">
            <input value={org.name} onChange={e => setOrg({ ...org, name: e.target.value })}
              className={inputCls} placeholder="Your Company Name" />
          </Field>
          <Field label="Legal Name">
            <input value={org.legalName ?? ''} onChange={e => setOrg({ ...org, legalName: e.target.value })}
              className={inputCls} placeholder="Registered legal name (if different)" />
            <p className={hintCls}>Only needed if your legal name differs from your trade name.</p>
          </Field>

          {/* Row 2: Website URL + Organization Type */}
          <Field label="Website URL *">
            <input value={org.url} onChange={e => setOrg({ ...org, url: e.target.value })}
              className={inputCls} placeholder="https://yoursite.com" />
          </Field>
          <Field label="Organization Type">
            <select
              value={org.organizationType ?? 'Organization'}
              onChange={e => setOrg({ ...org, organizationType: e.target.value })}
              className={`${inputCls} cursor-pointer`}
              style={{ colorScheme: 'dark' }}
            >
              {ORG_TYPES.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>

          {/* Logo URL — full width */}
          <Field label="Logo URL" className="sm:col-span-2">
            <input
              value={org.logo ?? ''}
              onChange={e => { setOrg({ ...org, logo: e.target.value }); if (logoUrlError) validateLogoUrl(e.target.value); }}
              onBlur={e => { const v = stripUrl(e.target.value); setOrg(o => ({ ...o, logo: v })); validateLogoUrl(v); }}
              className={`${inputCls} ${logoUrlError ? 'border-red-500/60' : ''}`}
              placeholder="https://yoursite.com/logo.png"
            />
            {logoUrlError
              ? <p className="mt-1 text-xs text-red-400">{logoUrlError}</p>
              : <p className={hintCls}>Paste the full URL of your logo image. Must be a publicly accessible PNG or JPG.</p>
            }
          </Field>

          {/* Social Profiles — full width */}
          <Field label="Social Profiles" className="sm:col-span-2">
            <div className="space-y-2">
              {(org.sameAs ?? []).map((profileUrl, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={profileUrl}
                    onChange={e => updateSameAs(i, e.target.value)}
                    className={inputCls}
                    placeholder="https://linkedin.com/company/yourcompany"
                  />
                  <button
                    type="button"
                    onClick={() => removeSameAs(i)}
                    className="flex-shrink-0 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addSameAs}
                className="flex items-center gap-1.5 text-sm text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors py-1"
              >
                <Plus className="w-4 h-4" />
                Add Social Profile
              </button>
            </div>
          </Field>

          {/* Description — full width textarea */}
          <Field label="Description" className="sm:col-span-2">
            <textarea
              value={org.description ?? ''}
              onChange={e => setOrg({ ...org, description: e.target.value })}
              className={`${inputCls} resize-none`}
              rows={3}
              placeholder="Brief description of your organization"
            />
          </Field>

          {/* Contact Email + Phone */}
          <Field label="Contact Email">
            <input value={org.email ?? ''} onChange={e => setOrg({ ...org, email: e.target.value })}
              className={inputCls} placeholder="contact@yoursite.com" />
          </Field>
          <Field label="Contact Phone">
            <CountryPhoneInput
              value={org.phone ?? ''}
              onChange={val => setOrg({ ...org, phone: val })}
            />
          </Field>

          {/* Founding Date */}
          <Field label="Founding Date">
            <input
              type="date"
              value={org.foundingDate ?? ''}
              onChange={e => setOrg({ ...org, foundingDate: e.target.value })}
              className={inputCls}
              style={{ colorScheme: 'dark' }}
            />
          </Field>
        </div>

        {/* Contact Points */}
        <div className="mt-6 pt-5 border-t border-[var(--bg-tertiary)]">
          <div className="mb-3">
            <h4 className="text-sm font-semibold">Contact Points</h4>
            <p className="text-xs text-white/40 mt-0.5">
              Define contact points by purpose (sales, support, press). Helps AI agents and search engines route enquiries correctly. At least one email or phone required per row. Entity-level contact points take priority over any set on the Contact page.
            </p>
          </div>
          <ContactPointsEditor
            points={org.contactPoints ?? []}
            onChange={pts => setOrg(prev => ({ ...prev, contactPoints: pts }))}
            context="org"
          />
        </div>

        {/* Areas of Expertise */}
        <div className="mt-6 pt-5 border-t border-[var(--bg-tertiary)]">
          <div className="mb-3">
            <h4 className="text-sm font-semibold">Areas of Expertise</h4>
            <p className="text-xs text-white/40 mt-0.5">
              Subject areas or topics this organisation is known for. Used as E-E-A-T signals in schema — helps AI and search engines understand what your organisation covers.
            </p>
          </div>
          <div className="space-y-2">
            {(org.knows_about ?? []).map((topic, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={topic}
                  onChange={e => updateKnowsAbout(i, e.target.value)}
                  className={inputCls}
                  placeholder="e.g. cybersecurity, cloud infrastructure"
                />
                <button
                  type="button"
                  onClick={() => removeKnowsAbout(i)}
                  className="flex-shrink-0 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addKnowsAbout}
              className="flex items-center gap-1.5 text-sm text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors py-1"
            >
              <Plus className="w-4 h-4" />
              Add Expertise Area
            </button>
          </div>
        </div>

        {/* Location & Service Area */}
        <div className="mt-6 pt-5 border-t border-[var(--bg-tertiary)]">
          <div className="mb-4">
            <h4 className="text-sm font-semibold">Location &amp; Service Area</h4>
            <p className="text-xs text-white/40 mt-0.5">
              Include your full address to enable LocalBusiness schema eligibility and confirm your geographic presence.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Street Address" className="sm:col-span-2">
              <input value={org.streetAddress ?? ''} onChange={e => setOrg({ ...org, streetAddress: e.target.value })}
                className={inputCls} placeholder="123 Main St" />
            </Field>
            <Field label="City">
              <input value={org.city ?? ''} onChange={e => setOrg({ ...org, city: e.target.value })}
                className={inputCls} placeholder="Enter city" />
            </Field>
            <Field label="State / Region">
              <input
                value={org.stateRegion ?? ''}
                onChange={e => { setOrg({ ...org, stateRegion: e.target.value }); setRegionNormalized(null); }}
                onBlur={e => {
                  const code = normalizeSubdivision(e.target.value, org.country ?? undefined);
                  if (code) {
                    setOrg(prev => ({ ...prev, stateRegion: code }));
                    setRegionNormalized(code);
                    setTimeout(() => setRegionNormalized(null), 3000);
                  }
                }}
                className={inputCls}
                placeholder="e.g. MN, ENG, ON, NSW"
              />
              {regionNormalized
                ? <p className="mt-1 text-xs text-green-400">Normalized to {regionNormalized}</p>
                : <p className={hintCls}>ISO 3166-2 subdivision code — e.g. MN (Minnesota), ENG (England), ON (Ontario), NSW (New South Wales). Enter the full name and it will be converted automatically.</p>
              }
            </Field>
            <Field label="Postal Code">
              <input value={org.postalCode ?? ''} onChange={e => setOrg({ ...org, postalCode: e.target.value })}
                className={inputCls} placeholder="55347" />
            </Field>
            <Field label="Country">
              <CountrySelectInput
                value={org.country ?? ''}
                onChange={code => setOrg({ ...org, country: code })}
              />
            </Field>
            <Field label="Area Served" className="sm:col-span-2">
              <input value={org.areaServed ?? ''} onChange={e => setOrg({ ...org, areaServed: e.target.value })}
                className={inputCls} placeholder="US, CA, AU" />
              <p className={hintCls}>Your service geography — e.g. "US", "CA", or comma-separated like "US, CA, AU".</p>
            </Field>
          </div>
        </div>

        {/* Location image */}
        <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)]">
          <p className="text-sm font-medium mb-3">Location Image</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Location / Building Image URL" className="sm:col-span-2">
              <input
                type="url"
                value={org.locationImage?.url ?? ''}
                onChange={e => {
                  const url = e.target.value;
                  setOrg(prev => ({
                    ...prev,
                    locationImage: url
                      ? { ...prev.locationImage, url }
                      : undefined,
                  }));
                }}
                onBlur={e => {
                  const url = stripUrl(e.target.value);
                  setOrg(prev => ({
                    ...prev,
                    locationImage: url
                      ? { ...prev.locationImage, url }
                      : undefined,
                  }));
                }}
                className={inputCls}
                placeholder="https://example.com/office-photo.jpg"
              />
              <p className={hintCls}>
                Enter the URL of your office, storefront, or location photo as it appears on your Contact page.
                Helps search engines and AI verify your physical location and stops bots from repeatedly
                re-evaluating unidentified images on your Contact page. Especially important for local businesses
                with a physical location.
              </p>
            </Field>
            <Field label="Location Image Caption" className="sm:col-span-2">
              <input
                value={org.locationImage?.caption ?? ''}
                onChange={e => {
                  const caption = e.target.value;
                  setOrg(prev => ({
                    ...prev,
                    locationImage: prev.locationImage?.url
                      ? { ...prev.locationImage, caption: caption || undefined }
                      : prev.locationImage,
                  }));
                }}
                className={inputCls}
                placeholder='e.g. "Main office entrance, Golden Valley MN"'
                disabled={!org.locationImage?.url}
              />
              <p className={hintCls}>
                {!org.locationImage?.url
                  ? 'Enter an image URL above before adding a caption.'
                  : 'Describe what the image shows — e.g. "Main office entrance, Golden Valley MN" or "Downtown storefront, Chicago IL." ' +
                    'This caption is read by search engines and AI to understand what the image depicts and connect it to your declared location.'}
              </p>
            </Field>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)] flex justify-end">
          <button
            onClick={() => handleSaveSection('Organization')}
            disabled={saving !== 'idle' || savingSection !== null}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSection === 'Organization'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 text-white/60" />}
            {savingSection === 'Organization' ? 'Saving…' : 'Save Organization'}
          </button>
        </div>
      </Section>

      {/* Authors */}
      <Section title={`Authors (${authors.length})`} icon={<Building2 className="w-4 h-4" />}>
        {/* Action buttons */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {cmsImportLabel(globalSettings.cmsType) && (
            <button
              type="button"
              onClick={handleImportAuthors}
              disabled={importingAuthors}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50"
            >
              {importingAuthors
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Plus className="w-3.5 h-3.5" />}
              {cmsImportLabel(globalSettings.cmsType)}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              const newIdx = authors.length;
              setAuthors(prev => [...prev, { name: '' }]);
              setExpandedAuthorIdx(newIdx);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Author Manually
          </button>
        </div>

        {/* Author cards */}
        <div className="space-y-2">
          {authors.map((a, i) => (
            <div key={i} className={`rounded-lg border overflow-hidden ${
              expandedAuthorIdx === i
                ? 'border-[#ffdc18]/30 bg-[var(--bg-tertiary)]'
                : 'border-white/5 bg-[var(--bg-tertiary)]'
            }`}>
              {/* Collapsed summary row */}
              {expandedAuthorIdx !== i ? (
                <div className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{a.name || 'Unnamed Author'}</span>
                      {a.isFounder && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#ffdc18]/20 text-[#ffdc18]">Founder</span>
                      )}
                      {a.jobTitle && <span className="text-xs text-white/50">· {a.jobTitle}</span>}
                    </div>
                    {a.url && <p className="text-xs text-white/40 mt-0.5 truncate">{a.url}</p>}
                    {(a.authorMapping ?? []).filter(Boolean).length > 0 && (
                      <p className="text-xs text-white/30 mt-0.5">
                        Matches author: {(a.authorMapping ?? []).filter(Boolean).map(m => `"${m}"`).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => setExpandedAuthorIdx(i)}
                      className="px-2.5 py-1 text-xs border border-white/20 rounded hover:bg-white/10 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => {
                        setAuthors(authors.filter((_, j) => j !== i));
                        if (expandedAuthorIdx === i) setExpandedAuthorIdx(null);
                        else if (expandedAuthorIdx !== null && expandedAuthorIdx > i) setExpandedAuthorIdx(expandedAuthorIdx - 1);
                      }}
                      className="p-1 text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                /* Expanded edit form */
                <div>
                  {/* Edit header */}
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                    <span className="text-sm font-medium">{a.name || 'New Author'}</span>
                    <button
                      onClick={() => setExpandedAuthorIdx(null)}
                      className="text-xs text-white/40 hover:text-white transition-colors"
                    >
                      Done
                    </button>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                      {/* Author Name */}
                      <Field label="Author Name *" className="sm:col-span-2">
                        <input value={a.name} onChange={e => updateAuthor(i, { name: e.target.value })}
                          className={inputCls} placeholder="Author name" />
                      </Field>

                      {/* Author Mapping — full width */}
                      <Field label="Author Mapping" className="sm:col-span-2">
                        <p className="text-xs text-white/40 mb-2">
                          Enter the username(s), email(s) or ID(s) your CMS uses to identify this author on blog posts.
                        </p>
                        <div className="space-y-2">
                          {getAuthorArr(a, 'authorMapping').map((m, j) => (
                            <div key={j} className="flex items-center gap-2">
                              <input value={m} onChange={e => updateAuthorArr(i, 'authorMapping', j, e.target.value)}
                                className={inputCls} placeholder="username, email, or ID" />
                              <button type="button" onClick={() => removeAuthorArrItem(i, 'authorMapping', j)}
                                className="flex-shrink-0 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button type="button" onClick={() => addAuthorArrItem(i, 'authorMapping')}
                            className="flex items-center gap-1.5 text-sm text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors py-1">
                            <Plus className="w-4 h-4" /> Add Mapping Entry
                          </button>
                        </div>
                      </Field>

                      {/* Author Profile URL */}
                      <Field label="Author Profile URL" className="sm:col-span-2">
                        <input value={a.url ?? ''} onChange={e => updateAuthor(i, { url: e.target.value })}
                          className={inputCls} placeholder="https://yoursite.com/author/name" />
                        <p className={hintCls}>Link to author's bio or about page.</p>
                      </Field>

                      {/* Author Image URL */}
                      <Field label="Author Image URL" className="sm:col-span-2">
                        <input value={a.image ?? ''} onChange={e => updateAuthor(i, { image: e.target.value })}
                          onBlur={e => updateAuthor(i, { image: stripUrl(e.target.value) })}
                          className={inputCls} placeholder="https://yoursite.com/author-headshot.jpg" />
                        <p className={hintCls}>Paste the full URL of the author's headshot or profile image.</p>
                      </Field>

                      {/* Job Title */}
                      <Field label="Job Title">
                        <input value={a.jobTitle ?? ''} onChange={e => updateAuthor(i, { jobTitle: e.target.value })}
                          className={inputCls} placeholder="CEO, Content Writer, Marketing Director" />
                        <p className={hintCls}>e.g., CEO, Content Writer, Marketing Director</p>
                      </Field>

                      {/* Founder checkbox */}
                      <Field label="Founder">
                        <label className="flex items-center gap-2 cursor-pointer mt-2">
                          <input
                            type="checkbox"
                            checked={a.isFounder ?? false}
                            onChange={e => updateAuthor(i, { isFounder: e.target.checked })}
                            className="w-4 h-4 rounded accent-[#ffdc18]"
                          />
                          <span className="text-sm font-medium">Founder</span>
                        </label>
                        <p className={hintCls}>Check if this person is a founder of the organization.</p>
                      </Field>

                      {/* Description */}
                      <Field label="Description" className="sm:col-span-2">
                        <textarea value={a.description ?? ''} onChange={e => updateAuthor(i, { description: e.target.value })}
                          className={`${inputCls} resize-none`} rows={3} placeholder="Author bio" />
                      </Field>

                      {/* Social Profiles */}
                      <Field label="Social Profiles" className="sm:col-span-2">
                        <div className="space-y-2">
                          {getAuthorArr(a, 'sameAs').map((url, j) => (
                            <div key={j} className="flex items-center gap-2">
                              <input value={url} onChange={e => updateAuthorArr(i, 'sameAs', j, e.target.value)}
                                className={inputCls} placeholder="https://linkedin.com/in/authorname" />
                              <button type="button" onClick={() => removeAuthorArrItem(i, 'sameAs', j)}
                                className="flex-shrink-0 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button type="button" onClick={() => addAuthorArrItem(i, 'sameAs')}
                            className="flex items-center gap-1.5 text-sm text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors py-1">
                            <Plus className="w-4 h-4" /> Add Social Profile
                          </button>
                        </div>
                      </Field>

                      {/* Topics of Expertise */}
                      <Field label="Topics of Expertise (knowsAbout)" className="sm:col-span-2">
                        <p className="text-xs text-white/40 mb-2">
                          Subject areas this author is expert in — used as E-E-A-T signals in schema.
                        </p>
                        <div className="space-y-2">
                          {getAuthorArr(a, 'knowsAbout').map((topic, j) => (
                            <div key={j} className="flex items-center gap-2">
                              <input value={topic} onChange={e => updateAuthorArr(i, 'knowsAbout', j, e.target.value)}
                                className={inputCls} placeholder="e.g. AI product development" />
                              <button type="button" onClick={() => removeAuthorArrItem(i, 'knowsAbout', j)}
                                className="flex-shrink-0 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                          <button type="button" onClick={() => addAuthorArrItem(i, 'knowsAbout')}
                            className="flex items-center gap-1.5 text-sm text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors py-1">
                            <Plus className="w-4 h-4" /> Add Topic
                          </button>
                        </div>
                      </Field>

                      {/* Contact Email */}
                      <Field label="Contact Email">
                        <input value={a.email ?? ''} onChange={e => updateAuthor(i, { email: e.target.value })}
                          className={inputCls} placeholder="author@yoursite.com" />
                      </Field>

                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          {authors.length === 0 && (
            <p className="text-sm text-white/40 py-2">
              No authors added yet. Import from your CMS or add one manually.
            </p>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)] flex justify-end">
          <button
            onClick={() => handleSaveSection('Authors')}
            disabled={saving !== 'idle' || savingSection !== null}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSection === 'Authors'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 text-white/60" />}
            {savingSection === 'Authors' ? 'Saving…' : 'Save Authors'}
          </button>
        </div>
      </Section>

      {/* Products & Services */}
      <Section title={`Products & Services (${products.length})`} icon={<Globe className="w-4 h-4" />}>
        <p className="text-sm text-white/50 mb-4">
          Define products and services your blog posts link to. Each entry gets a Schema Type so the generator knows whether it is software, a service, a physical product, or a course.
        </p>

        {/* Add button */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => {
              const newIdx = products.length;
              setProducts(prev => [...prev, { schemaType: '', name: '', url: '' }]);
              setExpandedProductIdx(newIdx);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Product / Service
          </button>
        </div>

        {/* Product cards */}
        <div className="space-y-2">
          {products.map((p, i) => {
            const showFeatures        = p.schemaType === 'SoftwareApplication' || p.schemaType === 'Course';
            const showCourseMeta      = p.schemaType === 'Course';
            const showSoftwareDetails = p.schemaType === 'SoftwareApplication';
            const typeLabel           = PRODUCT_SCHEMA_TYPE_LABELS[p.schemaType] ?? p.schemaType;

            return (
              <div key={i} className={`rounded-lg border overflow-hidden ${
                expandedProductIdx === i
                  ? 'border-[#ffdc18]/30 bg-[var(--bg-tertiary)]'
                  : 'border-white/5 bg-[var(--bg-tertiary)]'
              }`}>
                {expandedProductIdx !== i ? (
                  /* Collapsed summary */
                  <div className="flex items-start gap-3 px-4 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{p.name || 'Unnamed Product/Service'}</span>
                        {p.schemaType && (
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white/10 text-white/60">{typeLabel}</span>
                        )}
                      </div>
                      {p.url && <p className="text-xs text-white/40 mt-0.5 truncate">{p.url}</p>}
                      {p.price && <p className="text-xs text-white/30 mt-0.5">${p.price} {p.currency || 'USD'}</p>}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setExpandedProductIdx(i)}
                        className="px-2.5 py-1 text-xs border border-white/20 rounded hover:bg-white/10 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setProducts(products.filter((_, j) => j !== i));
                          if (expandedProductIdx === i) setExpandedProductIdx(null);
                          else if (expandedProductIdx !== null && expandedProductIdx > i) setExpandedProductIdx(expandedProductIdx - 1);
                        }}
                        className="p-1 text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Expanded edit form */
                  <div>
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                      <span className="text-sm font-medium">{p.name || 'New Product / Service'}</span>
                      <button
                        onClick={() => setExpandedProductIdx(null)}
                        className="text-xs text-white/40 hover:text-white transition-colors"
                      >
                        Done
                      </button>
                    </div>
                    <div className="p-4 space-y-4">

                      {/* Schema Type — full width */}
                      <Field label="Schema Type / Offering Type *">
                        <select
                          value={p.schemaType}
                          onChange={e => updateProduct(i, { schemaType: e.target.value })}
                          className={`${inputCls} cursor-pointer`}
                          style={{ colorScheme: 'dark' }}
                        >
                          {PRODUCT_SCHEMA_TYPES.map(t => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                        <p className={hintCls}>Determines the schema.org @type and which page this entry appears on.</p>
                      </Field>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                        {/* Name */}
                        <Field label="Name *">
                          <input value={p.name} onChange={e => updateProduct(i, { name: e.target.value })}
                            className={inputCls} placeholder="Product or service name" />
                        </Field>

                        {/* Entity ID — read-only, used as linked_entity_id */}
                        <Field label="Entity ID">
                          <div className="px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm font-mono text-white/50 truncate">
                            {p.entityId || slugify(p.name) || <span className="italic text-white/30">enter a name first</span>}
                          </div>
                          <p className={hintCls}>
                            Auto-derived from name — used when linking sub-pages. Renaming this entity will break existing sub-page links.
                          </p>
                        </Field>

                        {/* URL */}
                        <Field label="URL *">
                          <CmsPageUrlPicker
                            pages={entityCmsPages}
                            value={p.url}
                            onChange={url => updateProduct(i, { url })}
                            placeholder="Select or enter page URL…"
                            required
                          />
                        </Field>

                        {/* Description — full width */}
                        <Field label="Description" className="sm:col-span-2">
                          <textarea value={p.description ?? ''} onChange={e => updateProduct(i, { description: e.target.value })}
                            className={`${inputCls} resize-none`} rows={3} placeholder="Describe this product or service" />
                        </Field>

                        {/* Image URL — full width */}
                        <Field label="Image URL" className="sm:col-span-2">
                          <input value={p.image ?? ''} onChange={e => updateProduct(i, { image: e.target.value })}
                            onBlur={e => updateProduct(i, { image: stripUrl(e.target.value) })}
                            className={inputCls} placeholder="https://yoursite.com/product-image.png" />
                          <p className={hintCls}>Paste the full URL of the product or service image.</p>
                        </Field>

                        {/* Price + Currency on same row */}
                        <Field label="Price">
                          <div className="flex gap-2">
                            <input
                              value={p.price ?? ''}
                              onChange={e => updateProduct(i, { price: e.target.value })}
                              className={`${inputCls} flex-1`}
                              placeholder="497"
                              type="number"
                              min="0"
                              step="0.01"
                            />
                            <select
                              value={p.currency ?? 'USD'}
                              onChange={e => updateProduct(i, { currency: e.target.value })}
                              className="px-2 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#ffdc18]/50 cursor-pointer"
                              style={{ colorScheme: 'dark' }}
                            >
                              {CURRENCIES.map(c => (
                                <option key={c.code} value={c.code}>{c.code}</option>
                              ))}
                            </select>
                          </div>
                        </Field>

                        {/* SKU */}
                        <Field label="SKU">
                          <input value={p.sku ?? ''} onChange={e => updateProduct(i, { sku: e.target.value })}
                            className={inputCls} placeholder="VZX-PRO-WP" />
                        </Field>

                      </div>

                      {/* Feature List — SoftwareApplication + Course only */}
                      {showFeatures && (
                        <Field label="Feature List">
                          <div className="space-y-2">
                            {getProductArr(p, 'features').map((feat, j) => (
                              <div key={j} className="flex items-center gap-2">
                                <input value={feat} onChange={e => updateProductArr(i, 'features', j, e.target.value)}
                                  className={inputCls} placeholder="Feature description" />
                                <button type="button" onClick={() => removeProductArrItem(i, 'features', j)}
                                  className="flex-shrink-0 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                            <button type="button" onClick={() => addProductArrItem(i, 'features')}
                              className="flex items-center gap-1.5 text-sm text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors py-1">
                              <Plus className="w-4 h-4" /> Add Feature
                            </button>
                          </div>
                          <p className="mt-2 text-xs text-white/40">
                            {p.schemaType === 'Course'
                              ? 'List the subjects the course covers. One item per row.'
                              : 'One item per row.'}{' '}
                            Emitted as <code className="bg-white/10 px-1 rounded">feature_list</code> — takes priority over page-parsed content and description.
                          </p>
                        </Field>
                      )}

                      {/* Software Application Details — SoftwareApplication only */}
                      {showSoftwareDetails && (
                        <div className="pt-4 border-t border-[var(--bg-tertiary)]">
                          <p className="text-xs font-semibold uppercase tracking-wider text-white/40 mb-4">Software Application Details</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                            <Field label="Software Version (optional)" className="sm:col-span-2">
                              <input value={p.softwareVersion ?? ''} onChange={e => updateProduct(i, { softwareVersion: e.target.value })}
                                className={inputCls} placeholder="1.25.7" />
                              <p className={hintCls}>Current version number. Maps to <code className="bg-white/10 px-1 rounded">softwareVersion</code> in schema.</p>
                            </Field>

                            <Field label="Operating System / Platform (optional)" className="sm:col-span-2">
                              <input value={p.operatingSystem ?? ''} onChange={e => updateProduct(i, { operatingSystem: e.target.value })}
                                className={inputCls} placeholder="Web" />
                              <p className={hintCls}>Platform or OS this app runs on. Use "WordPress" for WP plugins, "Web" for SaaS/browser apps. Maps to <code className="bg-white/10 px-1 rounded">operatingSystem</code> in schema.</p>
                            </Field>

                            <Field label="Screenshot URLs (optional)" className="sm:col-span-2">
                              <div className="space-y-2">
                                {(p.screenshotUrls ?? []).map((url, j) => (
                                  <div key={j} className="flex items-center gap-2">
                                    <input value={url} onChange={e => {
                                      const next = [...(p.screenshotUrls ?? [])];
                                      next[j] = e.target.value;
                                      updateProduct(i, { screenshotUrls: next });
                                    }}
                                      className={inputCls} placeholder="https://example.com/screenshot.png" />
                                    <button type="button" onClick={() => {
                                      const next = (p.screenshotUrls ?? []).filter((_, k) => k !== j);
                                      updateProduct(i, { screenshotUrls: next });
                                    }}
                                      className="flex-shrink-0 p-2 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-400/10 transition-colors">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                                <button type="button" onClick={() => updateProduct(i, { screenshotUrls: [...(p.screenshotUrls ?? []), ''] })}
                                  className="flex items-center gap-1.5 text-sm text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors py-1">
                                  <Plus className="w-4 h-4" /> Add Screenshot URL
                                </button>
                              </div>
                              <p className="mt-2 text-xs text-white/40">URL of an app screenshot. Recommended by Google for SoftwareApplication rich results.</p>
                            </Field>

                            <div className="sm:col-span-2">
                              <p className="text-xs font-medium text-white/60 mb-3">Aggregate Rating (optional)</p>
                              <p className="text-xs text-white/40 mb-3">Required for Google star-rating rich results. Both average rating and count must be filled for the rating to be included in schema.</p>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                <Field label="Average Rating (1–5)">
                                  <input
                                    type="number" min="1" max="5" step="0.1"
                                    value={p.aggregateRating?.ratingValue ?? ''}
                                    onChange={e => {
                                      const val = e.target.value ? Number(e.target.value) : undefined;
                                      updateProduct(i, { aggregateRating: val !== undefined ? { ...p.aggregateRating, ratingValue: val, ratingCount: p.aggregateRating?.ratingCount ?? 0 } : undefined });
                                    }}
                                    className={inputCls} placeholder="4.5"
                                  />
                                </Field>
                                <Field label="Number of Ratings">
                                  <input
                                    type="number" min="0"
                                    value={p.aggregateRating?.ratingCount ?? ''}
                                    onChange={e => {
                                      const val = e.target.value ? Number(e.target.value) : undefined;
                                      updateProduct(i, { aggregateRating: val !== undefined ? { ...p.aggregateRating, ratingValue: p.aggregateRating?.ratingValue ?? 0, ratingCount: val } : undefined });
                                    }}
                                    className={inputCls} placeholder="127"
                                  />
                                </Field>
                                <Field label="Written Reviews">
                                  <input
                                    type="number" min="0"
                                    value={p.aggregateRating?.reviewCount ?? ''}
                                    onChange={e => {
                                      const val = e.target.value ? Number(e.target.value) : undefined;
                                      updateProduct(i, { aggregateRating: p.aggregateRating ? { ...p.aggregateRating, reviewCount: val } : undefined });
                                    }}
                                    className={inputCls} placeholder="(optional)"
                                    disabled={!p.aggregateRating?.ratingValue}
                                  />
                                </Field>
                              </div>
                            </div>

                          </div>
                        </div>
                      )}

                      {/* Course Details — Course only */}
                      {showCourseMeta && (
                        <div className="pt-4 border-t border-[var(--bg-tertiary)]">
                          <p className="text-xs font-semibold uppercase tracking-wider text-white/40 mb-4">Course Details</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                            <Field label="Course Mode">
                              <select
                                value={p.courseMode ?? 'Online'}
                                onChange={e => updateProduct(i, { courseMode: e.target.value })}
                                className={`${inputCls} cursor-pointer`}
                                style={{ colorScheme: 'dark' }}
                              >
                                {COURSE_MODES.map(m => (
                                  <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                              </select>
                              <p className={hintCls}>How the course is delivered. Defaults to online if not specified.</p>
                            </Field>

                            <Field label="Language (optional)">
                              <LanguageTagInput
                                value={typeof p.inLanguage === 'string' ? p.inLanguage : (p.inLanguage ?? []).join(', ')}
                                onChange={val => updateProduct(i, { inLanguage: val })}
                                placeholder="en-US"
                              />
                              <p className={hintCls}>BCP 47 language tag. Type a language name or code — e.g. "en-US", "es-MX". Maps to <code className="bg-white/10 px-1 rounded">inLanguage</code> in schema.</p>
                            </Field>

                            <Field label="Duration (ISO 8601)">
                              <input value={p.duration ?? ''} onChange={e => updateProduct(i, { duration: e.target.value })}
                                className={inputCls} placeholder="P12M" />
                              <p className={hintCls}>How long the course runs. Use: P12M = 12 months, P6W = 6 weeks, PT30H = 30 hours.</p>
                            </Field>

                            <Field label="Start Date (optional)">
                              <input
                                type="date"
                                value={p.courseStartDate ?? ''}
                                onChange={e => updateProduct(i, { courseStartDate: e.target.value })}
                                className={inputCls}
                                style={{ colorScheme: 'dark' }}
                              />
                              <p className={hintCls}>The next cohort or session start date. Required by Google for live cohort courses.</p>
                            </Field>

                            <Field label="Workload (optional)">
                              <input value={p.courseWorkload ?? ''} onChange={e => updateProduct(i, { courseWorkload: e.target.value })}
                                className={inputCls} placeholder="PT5H" />
                              <p className={hintCls}>Total effort required. Use ISO 8601 duration: PT40H = 40 hours total, PT5H = 5 hours/week. Required by Google for Course rich results.</p>
                            </Field>

                            <Field label="Location (optional)">
                              <input value={p.courseLocation ?? ''} onChange={e => updateProduct(i, { courseLocation: e.target.value })}
                                className={inputCls} placeholder="Online" />
                              <p className={hintCls}>Where the course is held. For online courses use "Online" or the platform name. Recommended by Google even for virtual courses.</p>
                            </Field>

                            <Field label="Instructor(s)" className="sm:col-span-2">
                              {authors.length === 0 ? (
                                <p className="text-sm text-white/40 py-1">
                                  No authors defined yet. Add instructors to the <strong>Authors</strong> section above, then return here to select them.
                                </p>
                              ) : (
                                <div className="space-y-1.5">
                                  {authors.map(a => (
                                    <label key={a.name} className="flex items-center gap-2 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={(p.instructors ?? []).includes(a.name)}
                                        onChange={() => toggleInstructor(i, a.name)}
                                        className="w-4 h-4 rounded accent-[#ffdc18]"
                                      />
                                      <span className="text-sm">{a.name}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                              <p className="mt-2 text-xs text-white/40">Select the author(s) who teach this course. Maps to <code className="bg-white/10 px-1 rounded">Course.instructor</code>.</p>
                            </Field>

                          </div>
                        </div>
                      )}

                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {products.length === 0 && (
            <p className="text-sm text-white/40 py-2">
              No products or services added yet. Click "Add Product / Service" to get started.
            </p>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)] flex justify-end">
          <button
            onClick={() => handleSaveSection('Products & Services')}
            disabled={saving !== 'idle' || savingSection !== null}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSection === 'Products & Services'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 text-white/60" />}
            {savingSection === 'Products & Services' ? 'Saving…' : 'Save Products & Services'}
          </button>
        </div>
      </Section>

      {/* Offers */}
      <Section title={`Offers — Optional (${offers.length})`} icon={<Globe className="w-4 h-4" />}>
        <p className="text-sm text-white/50 mb-4">
          Define offers, services, or sales pages that your blog posts link to. Great for: landing pages, consultation bookings, service pages, promos.
        </p>

        <div className="mb-4">
          <button
            type="button"
            onClick={openAddOfferModal}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add Offer
          </button>
        </div>

        <div className="space-y-2">
          {offers.map((o, i) => {
            const billingLabel = BILLING_PERIODS.find(b => b.value === o.billingPeriod)?.label ?? o.billingPeriod;
            const meta = [
              o.offerType,
              o.isFree ? 'Free' : (o.price ? `${o.price} ${o.currency ?? 'USD'}` : null),
              billingLabel,
            ].filter(Boolean).join(' · ');

            return (
              <div key={i} className="rounded-lg border border-white/5 bg-[var(--bg-tertiary)] flex items-start gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{o.name || 'Unnamed Offer'}</p>
                  {o.url && <p className="text-xs text-white/40 mt-0.5 truncate">{o.url}</p>}
                  {meta && <p className="text-xs text-white/30 mt-0.5">{meta}</p>}
                  {(o.appliesTo ?? []).length > 0 && (
                    <p className="text-xs text-[#ffdc18]/60 mt-0.5 truncate">
                      → {o.appliesTo!.join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => openEditOfferModal(i)}
                    className="px-2.5 py-1 text-xs border border-white/20 rounded hover:bg-white/10 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteOffer(i)}
                    className="p-1 text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          {offers.length === 0 && (
            <p className="text-sm text-white/40 py-2">
              No offers added yet. Click "+ Add Offer" to get started.
            </p>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)] flex justify-end">
          <button
            onClick={() => handleSaveSection('Offers')}
            disabled={saving !== 'idle' || savingSection !== null}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSection === 'Offers'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 text-white/60" />}
            {savingSection === 'Offers' ? 'Saving…' : 'Save Offers'}
          </button>
        </div>
      </Section>

      {/* Offer modal */}
      <OfferModal
        open={offerModalOpen}
        draft={offerDraft}
        isEditing={editingOfferIdx !== null}
        products={products}
        cmsPages={entityCmsPages}
        pricingPages={settingsPricingPages}
        allActionPages={settingsAllActionPages}
        appliesToSearch={appliesToSearch}
        onSearchChange={setAppliesToSearch}
        onDraftChange={patch => setOfferDraft(d => ({ ...d, ...patch }))}
        onToggleAppliesTo={toggleOfferAppliesTo}
        onSave={saveOffer}
        onClose={closeOfferModal}
      />

      {/* FAQ Settings */}
      <Section title="FAQ Settings" icon={<HelpCircle className="w-4 h-4" />}>
        {/* Enable extraction */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm">Enable FAQ extraction from posts</span>
          <button
            onClick={() => setGlobalSettings(g => ({ ...g, enableFaqExtraction: !g.enableFaqExtraction }))}
            className={`relative w-10 h-5 rounded-full transition-colors ${globalSettings.enableFaqExtraction ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${globalSettings.enableFaqExtraction ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        {/* Inject into post content */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-sm">Inject FAQ section into post content</span>
            <p className="text-xs text-white/40 mt-0.5">Inserts the FAQ section at the bottom of each post when served. Does not appear in the post editor.</p>
          </div>
          <button
            onClick={() => setGlobalSettings(g => ({ ...g, faqInjectionEnabled: !g.faqInjectionEnabled }))}
            className={`ml-4 flex-shrink-0 relative w-10 h-5 rounded-full transition-colors ${globalSettings.faqInjectionEnabled ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${globalSettings.faqInjectionEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <Field label="Max FAQs per post">
            <input
              type="number" min={1} max={20}
              value={globalSettings.maxFaqsPerPost}
              onChange={e => setGlobalSettings(g => ({ ...g, maxFaqsPerPost: parseInt(e.target.value) || 5 }))}
              className={`${inputCls} w-24`}
            />
          </Field>
          <Field label="FAQ Section Heading (H2)">
            <input
              type="text"
              value={globalSettings.faqSectionTitle ?? ''}
              onChange={e => setGlobalSettings(g => ({ ...g, faqSectionTitle: e.target.value }))}
              className={inputCls}
              placeholder="Frequently Asked Questions"
            />
            <p className={hintCls}>The H2 heading displayed above the FAQ list on each post.</p>
          </Field>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-2.5 rounded-lg bg-[#ffdc18]/5 border border-[#ffdc18]/20 px-4 py-3 text-xs text-[#ffdc18]/80">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>
            FAQs are regenerated automatically after each analysis and after every "Sync to Server Now".
            The FAQ section is injected into <code className="bg-white/10 px-1 rounded">footerHtml</code> — rendered server-side before the page is served, fully crawlable by Google and AI bots, and invisible in the HubSpot post editor.
            Customize the section styles in the <strong>FAQ Section CSS</strong> tab.
          </span>
        </div>
      </Section>

      {/* Brand Attribution (vizzex_brand_link / Halo mode) */}
      <Section
        title="Brand Attribution"
        icon={<Globe className="w-4 h-4" />}
        action={
          <button
            onClick={() => handleSaveSection('Brand Association')}
            disabled={saving !== 'idle' || savingSection !== null}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold border border-white/20 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingSection === 'Brand Association'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <CheckCircle2 className="w-4 h-4 text-white/60" />}
            {savingSection === 'Brand Association' ? 'Saving…' : 'Save Brand Association'}
          </button>
        }
      >
        <p className="text-xs text-white/50 mb-4">
          Controls whether your schema keeps VizzEx brand claims. This is independent of the{' '}
          <span className="text-white/70">Enable Schema Output</span> toggle above.
        </p>

        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-sm">Associate this site with VizzEx Signal Architecture</span>
            <p className="text-xs text-white/40 mt-0.5 leading-relaxed">
              When enabled, the schema service keeps VizzEx <code className="bg-white/10 px-1 rounded">isBasedOn</code> brand
              claims. You must also place the attribution link below in your site footer once, site-wide. When
              disabled, those brand claims are omitted and no footer attribution is needed.
            </p>
          </div>
          <button
            onClick={() => setGlobalSettings(g => ({ ...g, vizzexBrandLink: !g.vizzexBrandLink }))}
            disabled={globalSettings.isVizzexHost}
            className={`ml-4 flex-shrink-0 relative w-10 h-5 rounded-full transition-colors disabled:opacity-50 ${globalSettings.vizzexBrandLink ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${globalSettings.vizzexBrandLink ? 'translate-x-5' : 'translate-x-0'}`} />
          </button>
        </div>

        {globalSettings.isVizzexHost && (
          <div className="flex items-start gap-2.5 rounded-lg bg-white/5 border border-white/10 px-4 py-3 text-xs text-white/60 mb-4">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>This site's domain contains <code className="bg-white/10 px-1 rounded">vizzex.ai</code>, so brand attribution is always on regardless of this toggle.</span>
          </div>
        )}

        {/* Footer attribution */}
        <div className="mt-4 pt-4 border-t border-[var(--bg-tertiary)]">
          <p className="text-sm font-medium text-white mb-1">Footer attribution</p>
          <p className="text-xs text-white/50 leading-relaxed mb-3">
            Place this HTML once in your theme's global footer (footer template, site-wide footer widget, or
            equivalent). It will then appear on every page. Do not change the statement text, link URL, or anchor
            label. The link opens in a new tab. Keep it visible and crawlable — not hidden with CSS. Do not enable
            the toggle above until this code is placed in your site footer.
          </p>

          <div className="relative">
            <button
              onClick={handleCopyFooterHtml}
              className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs bg-white/10 hover:bg-white/15 text-white/70 hover:text-white transition-colors"
            >
              {footerHtmlCopied
                ? <><Check className="w-3.5 h-3.5 text-[#4ade80]" /><span className="text-[#4ade80]">Copied</span></>
                : <><Copy className="w-3.5 h-3.5" /><span>Copy HTML</span></>}
            </button>
            <pre className="bg-black/30 border border-white/10 rounded-lg p-4 pr-28 text-[11px] leading-relaxed font-mono text-white/70 overflow-x-auto whitespace-pre">
              {VIZZEX_ATTRIBUTION_HTML}
            </pre>
          </div>
        </div>

        {globalSettings.vizzexBrandLink && (
          <div className="flex items-start gap-2.5 rounded-lg bg-[#ffdc18]/5 border border-[#ffdc18]/20 px-4 py-3 text-xs text-[#ffdc18]/80 mt-4">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Brand association is enabled — a mismatch between this setting and the footer credit above is treated
              as "Ghost Schema" by AI crawlers. Make sure the footer code is live on every page.
            </span>
          </div>
        )}
      </Section>

      {/* Action bar */}
      <div className="space-y-3">
        {/* Row 1: primary sync button + message */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleSync}
            disabled={saving !== 'idle' || savingSection !== null}
            className={btnPrimary}
          >
            {saving === 'syncing'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Code className="w-4 h-4" />}
            {saving === 'syncing' ? 'Syncing…' : 'Sync to Server Now'}
          </button>

          {message && (
            <span className={`text-sm whitespace-pre-wrap ${
              message.type === 'success' ? 'text-green-400' :
              message.type === 'warning' ? 'text-yellow-400' :
              message.type === 'info'    ? 'text-blue-400' :
              'text-red-400'
            }`}>
              {message.text}
            </span>
          )}
        </div>

        {/* Row 2: regenerate buttons (dev tools) */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={handleRegeneratePosts}
            disabled={saving !== 'idle' || savingSection !== null}
            className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-50 transition-colors"
          >
            {saving === 'regen-posts'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            {saving === 'regen-posts' ? 'Regenerating Posts…' : 'Regenerate Post Schema'}
          </button>

          <button
            onClick={handleRegeneratePages}
            disabled={saving !== 'idle' || savingSection !== null}
            className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 disabled:opacity-50 transition-colors"
          >
            {saving === 'regen-pages'
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            {saving === 'regen-pages' ? 'Regenerating Pages…' : 'Regenerate Page Schema'}
          </button>
        </div>

        {/* Timestamps */}
        <div className="flex flex-wrap gap-4 text-xs text-white/30">
          {lastSavedAt && (
            <span>Saved: {new Date(lastSavedAt).toLocaleString()}</span>
          )}
          {lastSyncAt && (
            <span>
              Last synced to server: {new Date(lastSyncAt).toLocaleString()}
              {configHash && <span className="ml-1.5 font-mono text-white/20">{configHash.slice(0, 8)}</span>}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab 2: Post Schema Status ─────────────────────────────────────────────

function PostsTab({ siteId }: { siteId: number }) {
  const [posts, setPosts]         = useState<PostSchemaRow[]>([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(true);
  const [lastFetchAt, setLastFetchAt] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const LIMIT = 50;

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const result = await schemaApi.listPostSchemas(siteId, { page: p, limit: LIMIT, status: statusFilter || undefined });
      setPosts(result.posts);
      setTotal(result.total);
      setPage(p);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [siteId, statusFilter]);

  useEffect(() => {
    schemaApi.getGlobalSettings(siteId).then(gs => setLastFetchAt(gs.lastPostSchemaFetchAt ?? null)).catch(() => {});
    load(1);
  }, [siteId, load]);

  const hasFailed = posts.some(p => p.injectionStatus === 'failed');

  const handleRetryFailed = async () => {
    setActionLoading(true);
    setActionMsg(null);
    try {
      const result = await schemaApi.injectPosts(siteId, posts.filter(p => p.injectionStatus === 'failed').map(p => p.postId));
      setActionMsg(`Done — ${result.injected} injected, ${result.failed} failed`);
      load(page);
    } catch { setActionMsg('Retry failed. Please try again.'); }
    finally { setActionLoading(false); }
  };

  const handleToggle = async (postId: string, enabled: boolean) => {
    await schemaApi.togglePostSchema(siteId, postId, enabled);
    load(page);
  };

  return (
    <div className="space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
        <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-300">Post schemas update automatically after each analysis</p>
          <p className="text-sm text-blue-300/70 mt-0.5">
            Schemas are generated during analysis and injected into your posts immediately. No manual action needed.
          </p>
          {lastFetchAt && (
            <p className="text-xs text-blue-300/50 mt-1">Last updated: {new Date(lastFetchAt).toLocaleString()}</p>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); load(1); }}
          className="px-3 py-1.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white focus:outline-none w-40"
        >
          <option value="">All statuses</option>
          <option value="injected">Injected</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
          <option value="disabled">Disabled</option>
        </select>

        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search posts by title…"
            className="w-full pl-8 pr-8 py-1.5 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#ffdc18]/40"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
            >✕</button>
          )}
        </div>

        {hasFailed && (
          <button onClick={handleRetryFailed} disabled={actionLoading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-lg hover:bg-yellow-500/30 transition-colors">
            {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Retry Failed
          </button>
        )}
        {actionMsg && <span className="text-sm text-white/60">{actionMsg}</span>}
      </div>

      {/* Table */}
      {(() => {
        const displayedPosts = search.trim()
          ? posts.filter(p =>
              (p.postTitle ?? p.postId).toLowerCase().includes(search.trim().toLowerCase())
            )
          : posts;

        if (loading) return (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>
        );
        if (posts.length === 0) return (
          <div className="text-center py-12 text-white/40">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No post schemas found. Run an analysis first.</p>
          </div>
        );
        if (displayedPosts.length === 0) return (
          <div className="text-center py-12 text-white/40">
            <Search className="w-6 h-6 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No posts match "<span className="text-white/60">{search}</span>"</p>
          </div>
        );
        return (
        <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bg-tertiary)]">
                <th className="px-4 py-3 text-left text-white/60 font-medium">Post</th>
                <th className="px-4 py-3 text-left text-white/60 font-medium">Status</th>
                <th className="px-4 py-3 text-left text-white/60 font-medium">Injected</th>
                <th className="px-4 py-3 text-center text-white/60 font-medium">Enabled</th>
              </tr>
            </thead>
            <tbody>
              {displayedPosts.map(row => (
                <tr key={row.postId} className="border-b border-[var(--bg-tertiary)]/50 hover:bg-[var(--bg-tertiary)]/30">
                  <td className="px-4 py-3">
                    <div className="flex items-baseline gap-2">
                      <p className="font-medium truncate max-w-xs">{row.postTitle || row.postId}</p>
                      {row.postUrl && (
                        <a
                          href={row.postUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-[#ffdc18]/60 hover:text-[#ffdc18] transition-colors shrink-0"
                        >
                          View ↗
                        </a>
                      )}
                    </div>
                    {row.injectionError && (
                      <p className="text-xs text-red-400 mt-0.5 truncate max-w-xs" title={row.injectionError}>
                        {row.injectionError}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <StatusIcon status={row.injectionStatus} />
                      {statusBadge(row.injectionStatus)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/50 text-xs">
                    {row.injectedAt ? new Date(row.injectedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleToggle(row.postId, !row.schemaDisplayEnabled)}
                      className={`relative w-9 h-5 rounded-full transition-colors ${row.schemaDisplayEnabled ? 'bg-[#ffdc18]' : 'bg-white/20'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${row.schemaDisplayEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {total > LIMIT && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--bg-tertiary)]">
              <span className="text-sm text-white/50">Showing {((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, total)} of {total}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => load(page - 1)}
                  className="px-3 py-1 text-sm bg-[var(--bg-tertiary)] rounded disabled:opacity-40">
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button disabled={page * LIMIT >= total} onClick={() => load(page + 1)}
                  className="px-3 py-1 text-sm bg-[var(--bg-tertiary)] rounded disabled:opacity-40">
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
        );
      })()}
    </div>
  );
}

// ─── Tab 3: Page Mapping ───────────────────────────────────────────────────

interface PageMappingState {
  cmsPageId:     string | null;
  subtype:       string | null;
  contactPoints: ContactPoint[];
}

function emptyMappingState(): PageMappingState {
  return { cmsPageId: null, subtype: null, contactPoints: [] };
}

// ─── BCP 47 Language Tag Combobox ────────────────────────────────────────────

const BCP47_LANGUAGES: { code: string; name: string }[] = [
  { code: 'af',    name: 'Afrikaans' },
  { code: 'ar',    name: 'Arabic' },
  { code: 'bg',    name: 'Bulgarian' },
  { code: 'bn',    name: 'Bengali' },
  { code: 'cs',    name: 'Czech' },
  { code: 'cy',    name: 'Welsh' },
  { code: 'da',    name: 'Danish' },
  { code: 'de',    name: 'German' },
  { code: 'de-AT', name: 'German (Austria)' },
  { code: 'de-CH', name: 'German (Switzerland)' },
  { code: 'el',    name: 'Greek' },
  { code: 'en',    name: 'English' },
  { code: 'en-AU', name: 'English (Australia)' },
  { code: 'en-CA', name: 'English (Canada)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'en-IE', name: 'English (Ireland)' },
  { code: 'en-NZ', name: 'English (New Zealand)' },
  { code: 'en-US', name: 'English (US)' },
  { code: 'en-ZA', name: 'English (South Africa)' },
  { code: 'es',    name: 'Spanish' },
  { code: 'es-AR', name: 'Spanish (Argentina)' },
  { code: 'es-CL', name: 'Spanish (Chile)' },
  { code: 'es-CO', name: 'Spanish (Colombia)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'et',    name: 'Estonian' },
  { code: 'fa',    name: 'Persian' },
  { code: 'fi',    name: 'Finnish' },
  { code: 'fr',    name: 'French' },
  { code: 'fr-BE', name: 'French (Belgium)' },
  { code: 'fr-CA', name: 'French (Canada)' },
  { code: 'fr-CH', name: 'French (Switzerland)' },
  { code: 'fr-FR', name: 'French (France)' },
  { code: 'gu',    name: 'Gujarati' },
  { code: 'he',    name: 'Hebrew' },
  { code: 'hi',    name: 'Hindi' },
  { code: 'hr',    name: 'Croatian' },
  { code: 'hu',    name: 'Hungarian' },
  { code: 'hy',    name: 'Armenian' },
  { code: 'id',    name: 'Indonesian' },
  { code: 'it',    name: 'Italian' },
  { code: 'it-CH', name: 'Italian (Switzerland)' },
  { code: 'ja',    name: 'Japanese' },
  { code: 'ka',    name: 'Georgian' },
  { code: 'kn',    name: 'Kannada' },
  { code: 'ko',    name: 'Korean' },
  { code: 'lt',    name: 'Lithuanian' },
  { code: 'lv',    name: 'Latvian' },
  { code: 'mk',    name: 'Macedonian' },
  { code: 'ml',    name: 'Malayalam' },
  { code: 'mr',    name: 'Marathi' },
  { code: 'ms',    name: 'Malay' },
  { code: 'mt',    name: 'Maltese' },
  { code: 'nl',    name: 'Dutch' },
  { code: 'nl-BE', name: 'Dutch (Belgium)' },
  { code: 'no',    name: 'Norwegian' },
  { code: 'pa',    name: 'Punjabi' },
  { code: 'pl',    name: 'Polish' },
  { code: 'pt',    name: 'Portuguese' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)' },
  { code: 'ro',    name: 'Romanian' },
  { code: 'ru',    name: 'Russian' },
  { code: 'sk',    name: 'Slovak' },
  { code: 'sl',    name: 'Slovenian' },
  { code: 'sq',    name: 'Albanian' },
  { code: 'sr',    name: 'Serbian' },
  { code: 'sv',    name: 'Swedish' },
  { code: 'sw',    name: 'Swahili' },
  { code: 'ta',    name: 'Tamil' },
  { code: 'te',    name: 'Telugu' },
  { code: 'th',    name: 'Thai' },
  { code: 'tl',    name: 'Filipino' },
  { code: 'tr',    name: 'Turkish' },
  { code: 'uk',    name: 'Ukrainian' },
  { code: 'ur',    name: 'Urdu' },
  { code: 'vi',    name: 'Vietnamese' },
  { code: 'zh',    name: 'Chinese' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'zh-HK', name: 'Chinese (Hong Kong)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'zu',    name: 'Zulu' },
];

/**
 * Searchable BCP 47 language tag combobox.
 * Accepts multiple codes (comma-separated). User can type a language name
 * ("English") or a code ("en-US") — matching suggestions appear in a dropdown.
 * Selecting a suggestion inserts/replaces the last typed token with the code.
 */
function LanguageTagInput({
  value,
  onChange,
  placeholder = 'e.g. en-US',
  className = '',
}: {
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  className?: string;
}) {
  const [query,      setQuery]      = useState('');
  const [open,       setOpen]       = useState(false);
  const [activeIdx,  setActiveIdx]  = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Tags are the already-committed codes (all but the last token while typing)
  const tags = value.split(',').map(s => s.trim()).filter(Boolean);

  const suggestions = query.trim().length > 0
    ? BCP47_LANGUAGES.filter(l =>
        l.name.toLowerCase().includes(query.toLowerCase()) ||
        l.code.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8)
    : [];

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const commitCode = (code: string) => {
    const existing = tags.filter(t => t !== query.trim());
    const next = [...existing, code].join(', ');
    onChange(next);
    setQuery('');
    setOpen(false);
    setActiveIdx(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && open && suggestions[activeIdx]) {
      e.preventDefault();
      commitCode(suggestions[activeIdx].code);
    }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    if (e.key === 'Backspace' && query === '' && tags.length > 0) {
      onChange(tags.slice(0, -1).join(', '));
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div
        className={`flex flex-wrap gap-1 items-center min-h-[36px] px-2 py-1.5
          bg-[var(--bg-tertiary)] border border-white/10 rounded-lg
          focus-within:border-[#ffdc18]/50 cursor-text`}
        onClick={() => (containerRef.current?.querySelector('input') as HTMLInputElement)?.focus()}
      >
        {tags.map(tag => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#ffdc18]/15 text-[#ffdc18] text-xs font-mono"
          >
            {tag}
            <button
              type="button"
              onClick={e => { e.stopPropagation(); onChange(tags.filter(t => t !== tag).join(', ')); }}
              className="text-[#ffdc18]/60 hover:text-[#ffdc18] leading-none"
              aria-label={`Remove ${tag}`}
            >×</button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          placeholder={tags.length === 0 ? placeholder : 'Add language…'}
          onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
          onFocus={() => { if (query) setOpen(true); }}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-[80px] bg-transparent text-xs text-white placeholder:text-white/50 outline-none py-0.5"
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-[var(--bg-secondary)] border border-white/15 rounded-lg shadow-xl overflow-auto max-h-48 text-xs">
          {suggestions.map((l, idx) => (
            <li
              key={l.code}
              onMouseDown={e => { e.preventDefault(); commitCode(l.code); }}
              className={`flex items-center justify-between px-3 py-2 cursor-pointer ${
                idx === activeIdx ? 'bg-[#ffdc18]/15 text-white' : 'text-white/80 hover:bg-white/5'
              }`}
            >
              <span>{l.name}</span>
              <code className="text-[#ffdc18]/80 bg-white/5 px-1.5 py-0.5 rounded font-mono">{l.code}</code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── ContactPointsEditor ────────────────────────────────────────────────────

const CONTACT_TYPES = [
  { value: 'customer_support',  label: 'Customer Support' },
  { value: 'sales',             label: 'Sales' },
  { value: 'technical_support', label: 'Technical Support' },
  { value: 'billing_support',   label: 'Billing Support' },
  { value: 'press',             label: 'Press' },
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_LABELS   = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function OpeningHoursEditor({
  value,
  onChange,
}: {
  value: OpeningHoursSpec | OpeningHoursSpec[] | null | undefined;
  onChange: (v: OpeningHoursSpec[] | null) => void;
}) {
  // Normalise: coerce single object → array, strip any legacy string values
  // (old data may have hours_available as a plain string from before this change)
  const schedules: OpeningHoursSpec[] = (() => {
    if (!value) return [];
    const arr = Array.isArray(value) ? value : [value];
    return arr.filter(
      (s): s is OpeningHoursSpec =>
        s !== null && typeof s === 'object' && Array.isArray((s as OpeningHoursSpec).day_of_week)
    );
  })();

  const addSchedule = () =>
    onChange([...schedules, { day_of_week: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '09:00', closes: '17:00' }]);

  const removeSchedule = (i: number) => {
    const next = schedules.filter((_, idx) => idx !== i);
    onChange(next.length > 0 ? next : null);
  };

  const updateSchedule = (i: number, patch: Partial<OpeningHoursSpec>) =>
    onChange(schedules.map((s, idx) => idx === i ? { ...s, ...patch } : s));

  const toggleDay = (i: number, day: string) => {
    const s = schedules[i];
    const days = s.day_of_week.includes(day)
      ? s.day_of_week.filter(d => d !== day)
      : [...s.day_of_week, day];
    updateSchedule(i, { day_of_week: days });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className={hintCls}>Hours of operation — add one row per distinct schedule</p>
        <button
          type="button"
          onClick={addSchedule}
          className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors flex items-center gap-1"
        >
          <span className="text-base leading-none">+</span> Add Schedule
        </button>
      </div>
      {schedules.length === 0 && (
        <p className="text-xs text-white/30 italic">No hours configured. Click "Add Schedule" to define hours.</p>
      )}
      {schedules.map((s, i) => (
        <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3 space-y-2">
          {/* Day toggles */}
          <div className="flex flex-wrap gap-1">
            {DAYS_OF_WEEK.map((day, di) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleDay(i, day)}
                className={`text-xs px-1.5 py-0.5 rounded font-medium transition-colors ${
                  s.day_of_week.includes(day)
                    ? 'bg-[#ffdc18] text-black'
                    : 'bg-white/10 text-white/50 hover:bg-white/20'
                }`}
              >
                {DAY_LABELS[di]}
              </button>
            ))}
          </div>
          {/* Opens / Closes */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-white/50">Opens</span>
              <input
                type="time"
                value={s.opens}
                onChange={e => updateSchedule(i, { opens: e.target.value })}
                className={inputCls + ' text-xs w-28'}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-white/50">Closes</span>
              <input
                type="time"
                value={s.closes}
                onChange={e => updateSchedule(i, { closes: e.target.value })}
                className={inputCls + ' text-xs w-28'}
              />
            </div>
            <button
              type="button"
              onClick={() => removeSchedule(i)}
              className="ml-auto text-xs text-red-400 hover:text-red-300 transition-colors"
            >
              Remove
            </button>
          </div>
          {/* Seasonal dates */}
          <details>
            <summary className={hintCls + ' cursor-pointer select-none'}>Seasonal dates (optional)</summary>
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-white/50">From</span>
                <input
                  type="date"
                  value={s.valid_from ?? ''}
                  onChange={e => updateSchedule(i, { valid_from: e.target.value || null })}
                  className={inputCls + ' text-xs'}
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-white/50">Through</span>
                <input
                  type="date"
                  value={s.valid_through ?? ''}
                  onChange={e => updateSchedule(i, { valid_through: e.target.value || null })}
                  className={inputCls + ' text-xs'}
                />
              </div>
            </div>
          </details>
        </div>
      ))}
    </div>
  );
}

function ContactPointsEditor({
  points,
  onChange,
  context = 'page',
}: {
  points: ContactPoint[];
  onChange: (pts: ContactPoint[]) => void;
  context?: 'org' | 'page';
}) {
  const addPoint = () =>
    onChange([...points, { contact_type: 'customer_support', email: '', telephone: '', available_language: '', hours_available: null }]);

  const removePoint = (i: number) =>
    onChange(points.filter((_, idx) => idx !== i));

  const updatePoint = (i: number, patch: Partial<ContactPoint>) =>
    onChange(points.map((p, idx) => idx === i ? { ...p, ...patch } : p));

  return (
    <div className="pl-1 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-white/50">
          {context === 'org'
            ? 'One row per contact channel — used as ContactPoint[] on Organization and Contact page.'
            : 'Contact points — used to generate ContactPage schema'}
        </p>
        <button
          type="button"
          onClick={addPoint}
          className="text-xs px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors flex items-center gap-1"
        >
          <span className="text-base leading-none">+</span> Add Contact Point
        </button>
      </div>
      {points.length === 0 && (
        <p className="text-xs text-white/30 italic">No contact points added. Click "Add Contact Point" to begin.</p>
      )}
      {points.map((pt, i) => (
        <div key={i} className="bg-[var(--bg-tertiary)] rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <select
              value={pt.contact_type}
              onChange={e => updatePoint(i, { contact_type: e.target.value })}
              className="text-xs bg-[var(--bg-secondary)] border border-white/10 rounded px-2 py-1 text-white"
            >
              {CONTACT_TYPES.map(ct => (
                <option key={ct.value} value={ct.value}>{ct.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => removePoint(i)}
              className="text-xs text-red-400 hover:text-red-300 px-1"
            >
              Remove
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="email"
              placeholder="Email (optional)"
              value={pt.email ?? ''}
              onChange={e => updatePoint(i, { email: e.target.value })}
              className={inputCls + ' text-xs'}
            />
            <CountryPhoneInput
              value={pt.telephone ?? ''}
              onChange={val => updatePoint(i, { telephone: val })}
              placeholder="555 000 0000"
            />
          </div>
          <div>
            <LanguageTagInput
              value={typeof pt.available_language === 'string' ? pt.available_language : (pt.available_language ?? []).join(', ')}
              onChange={val => updatePoint(i, { available_language: val })}
              placeholder="Language(s) e.g. en-US"
              className="text-xs"
            />
            <p className={hintCls}>Type a language name or code to search</p>
          </div>
          <OpeningHoursEditor
            value={pt.hours_available}
            onChange={v => updatePoint(i, { hours_available: v })}
          />
        </div>
      ))}
    </div>
  );
}

function PagesTab({ siteId }: { siteId: number }) {
  const [cmsPages, setCmsPages]     = useState<CmsPageOption[]>([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [cachedAt, setCachedAt]     = useState<string | null>(null);
  const [mapping, setMapping]       = useState<Record<string, PageMappingState>>({});
  const [pageSchemaRows, setPageSchemaRows] = useState<PageSchemaRow[]>([]);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage]       = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [progress, setProgress]     = useState<string | null>(null);
  const [savingOnly, setSavingOnly] = useState(false);
  const [saveMsg, setSaveMsg]       = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Entity counts for inline warnings
  const [productCount, setProductCount] = useState(0);
  const [offersWithoutLinkedIds, setOffersWithoutLinkedIds] = useState(0);

  // Sub-page, pricing, and glossary page mappings
  const [subPageMappings,      setSubPageMappings]      = useState<SubPageMappingEntry[]>([]);
  const [pricingMappings,      setPricingMappings]      = useState<PricingMappingEntry[]>([]);
  const [glossaryMappings,     setGlossaryMappings]     = useState<GlossaryMappingEntry[]>([]);
  const [glossaryTermMappings, setGlossaryTermMappings] = useState<GlossaryTermMappingEntry[]>([]);

  // All products/services for sub-page mapping UI
  const [allProducts, setAllProducts] = useState<SchemaProduct[]>([]);

  // Heading analysis state
  const [headingAnalyses, setHeadingAnalyses]           = useState<PageHeadingAnalysis[]>([]);
  const [analyzingPage, setAnalyzingPage]               = useState<string | null>(null);
  const [headingMsg, setHeadingMsg]                     = useState<{ type: 'success'|'error'; text: string } | null>(null);
  const [viewingAnalysis, setViewingAnalysis]           = useState<PageHeadingAnalysis | null>(null);

  const loadPagesList = useCallback(async (refresh = false) => {
    setPagesLoading(true);
    try {
      const result = await schemaApi.getCmsPages(siteId, refresh);
      setCmsPages(result.pages);
      setCachedAt(result.cachedAt);
      return result;
    } catch { return null; }
    finally { setPagesLoading(false); }
  }, [siteId]);

  // Auto-refresh the page list if the cache is absent or older than 2 hours.
  // Runs after the initial cached load so the tab opens fast and refreshes silently.
  const PAGES_STALE_MS = 2 * 60 * 60 * 1000;
  useEffect(() => {
    loadPagesList(false).then(result => {
      const age = result?.cachedAt
        ? Date.now() - new Date(result.cachedAt).getTime()
        : Infinity;
      if (age > PAGES_STALE_MS) {
        loadPagesList(true);
      }
    });
  }, [siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    Promise.all([
      schemaApi.getPageMapping(siteId),
      schemaApi.listPageHeadingAnalyses(siteId),
      schemaApi.getEntities(siteId),
    ]).then(([mappingResult, analysisResult, entityResult]) => {
      const m: Record<string, PageMappingState> = {};
      for (const row of mappingResult.mapping) {
        m[row.pageType] = {
          cmsPageId:     row.cmsPageId ?? null,
          subtype:       row.pageSubtype ?? null,
          contactPoints: Array.isArray(row.contactPoints) ? row.contactPoints : [],
        };
      }
      setMapping(m);
      setPageSchemaRows(mappingResult.mapping);
      setSubPageMappings(mappingResult.subPageMappings ?? []);
      setPricingMappings(mappingResult.pricingMappings ?? []);
      setGlossaryMappings(mappingResult.glossaryMappings ?? []);
      setGlossaryTermMappings(mappingResult.glossaryTermMappings ?? []);
      setHeadingAnalyses(analysisResult.analyses);

      const prods = entityResult.products ?? [];
      setProductCount(prods.length);
      setAllProducts(prods);

      const offersNoLink = (entityResult.offers ?? []).filter(
        (o: SchemaOffer) => !o.appliesTo || (o.appliesTo as string[]).length === 0
      ).length;
      setOffersWithoutLinkedIds(offersNoLink);
    }).catch(console.error).finally(() => setLoading(false));
  }, [siteId, loadPagesList]);

  // Auto-match entity URLs to CMS pages whenever both lists are available.
  // Only fills entries that have no cmsPageId — never overrides a saved selection.
  useEffect(() => {
    if (cmsPages.length === 0 || allProducts.length === 0) return;

    const toPath = (url: string) =>
      url.replace(/^https?:\/\/[^/]+/, '').replace(/\/$/, '').toLowerCase() || '/';

    setSubPageMappings(prev => {
      let changed = false;
      const next = allProducts.map(entity => {
        const entityId = entity.entityId || slugify(entity.name);
        const existing = prev.find(sp => sp.linkedEntityId === entityId);

        // Already mapped — keep as-is
        if (existing?.cmsPageId) return existing;

        if (!entity.url) {
          // No URL on entity — keep existing placeholder or return nothing (handled below)
          return existing ?? null;
        }

        const entityPath = toPath(entity.url);
        const matched = cmsPages.find(p => toPath(p.url) === entityPath && !p.isBlogListing);
        if (!matched) return existing ?? null;

        // Found a match — auto-fill
        changed = true;
        const entityPageType: 'product_page' | 'service_page' =
          isServiceEntity(entity) ? 'service_page' : 'product_page';
        return {
          pageType:       entityPageType,
          linkedEntityId: entityId,
          entityName:     entity.name,
          cmsPageId:      matched.id,
          cmsPageUrl:     matched.url,
          cmsPageName:    matched.name,
        } as SubPageMappingEntry;
      }).filter((sp): sp is SubPageMappingEntry => sp !== null);

      // Preserve any custom/manual rows (not tied to allProducts)
      const knownEntityIds = new Set(allProducts.map(e => e.entityId || slugify(e.name)));
      const customRows = prev.filter(sp => !knownEntityIds.has(sp.linkedEntityId));

      if (!changed && customRows.length === 0) return prev;
      return [...next, ...customRows];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsPages, allProducts]);

  const handleAnalyzePage = async (pageType: string, entityId?: string | null) => {
    const analyzeKey = entityId ? `${pageType}/${entityId}` : pageType;
    setAnalyzingPage(analyzeKey);
    setHeadingMsg(null);
    try {
      const result = await schemaApi.analyzePageHeadingStructure(siteId, pageType, entityId);
      const updated = await schemaApi.listPageHeadingAnalyses(siteId);
      setHeadingAnalyses(updated.analyses);
      setHeadingMsg({ type: 'success', text: `Analysis complete — score: ${result.score ?? 'N/A'}/100` });
    } catch (err: unknown) {
      setHeadingMsg({ type: 'error', text: `Analysis failed: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setAnalyzingPage(null);
    }
  };

  const getRowStatus = (pageType: string): PageSchemaRow | undefined =>
    pageSchemaRows.find(r => r.pageType === pageType);

  // ── Shared helper: build the three mapping payloads from current state ───────
  const buildMappingPayloads = () => {
    const mappingPayload: Record<string, PageMappingEntry | null> = {};
    for (const pt of PAGE_TYPES) {
      const state  = mapping[pt.key] ?? emptyMappingState();
      const pageId = state.cmsPageId;
      const page   = pageId ? cmsPages.find(p => p.id === pageId) : null;
      mappingPayload[pt.key] = pageId ? {
        cmsPageId:     pageId,
        cmsPageUrl:    page?.url ?? null,
        cmsPageName:   page?.name ?? null,
        pageSubtype:   pt.hasSubtype ? (state.subtype || null) : null,
        contactPoints: pt.hasContactPoints && state.contactPoints.length > 0
          ? state.contactPoints
          : null,
      } : null;
    }

    // Deduplicate sub-pages by linkedEntityId; derive pageType from entity schema type.
    const seenEntityIds = new Set<string>();
    const subPagesPayload = subPageMappings
      .filter(sp => sp.linkedEntityId && sp.cmsPageId)
      .filter(sp => {
        if (seenEntityIds.has(sp.linkedEntityId)) return false;
        seenEntityIds.add(sp.linkedEntityId);
        return true;
      })
      .map(sp => {
        const page   = sp.cmsPageId ? cmsPages.find(p => p.id === sp.cmsPageId) : null;
        const entity = allProducts.find(e => (e.entityId || slugify(e.name)) === sp.linkedEntityId);
        const pageType: 'product_page' | 'service_page' =
          entity && isServiceEntity(entity) ? 'service_page' : 'product_page';
        return {
          pageType,
          linkedEntityId: sp.linkedEntityId,
          cmsPageId:      sp.cmsPageId,
          cmsPageUrl:     page?.url  ?? sp.cmsPageUrl  ?? null,
          cmsPageName:    page?.name ?? sp.cmsPageName ?? null,
        };
      });

    const pricingPayload = pricingMappings.map(pp => {
      const page = pp.cmsPageId ? cmsPages.find(p => p.id === pp.cmsPageId) : null;
      return {
        cmsPageId:   pp.cmsPageId,
        cmsPageUrl:  page?.url  ?? pp.cmsPageUrl  ?? null,
        cmsPageName: page?.name ?? pp.cmsPageName ?? null,
      };
    });

    // Glossary roots
    const glossaryPayload: GlossaryMappingEntry[] = glossaryMappings
      .filter(gr => gr.cmsPageId)
      .map(gr => {
        const page = gr.cmsPageId ? cmsPages.find(p => p.id === gr.cmsPageId) : null;
        return {
          linkedEntityId:      gr.linkedEntityId      || null,
          cmsPageId:           gr.cmsPageId,
          cmsPageUrl:          page?.url  ?? gr.cmsPageUrl  ?? null,
          cmsPageName:         page?.name ?? gr.cmsPageName ?? null,
          hasGlossarySubpages: gr.hasGlossarySubpages,
          description:         gr.description         || null,
        };
      });

    // Glossary term sub-pages
    const glossaryTermsPayload: GlossaryTermMappingEntry[] = glossaryTermMappings
      .filter(gt => gt.linkedEntityId && gt.cmsPageId)
      .map(gt => {
        const page = gt.cmsPageId ? cmsPages.find(p => p.id === gt.cmsPageId) : null;
        return {
          linkedEntityId:      gt.linkedEntityId,
          cmsPageId:           gt.cmsPageId,
          cmsPageUrl:          page?.url  ?? gt.cmsPageUrl  ?? null,
          cmsPageName:         page?.name ?? gt.cmsPageName ?? null,
          parentGlossaryUrl:   gt.parentGlossaryUrl  ?? null,
          parentGlossaryName:  gt.parentGlossaryName ?? null,
          termCode:            gt.termCode            ?? null,
          description:         gt.description         ?? null,
        };
      });

    return { mappingPayload, subPagesPayload, pricingPayload, glossaryPayload, glossaryTermsPayload };
  };

  // ── Save mappings only (no schema generation) ────────────────────────────
  const handleSaveMappings = async () => {
    setSavingOnly(true);
    setSaveMsg(null);
    try {
      const { mappingPayload, subPagesPayload, pricingPayload, glossaryPayload, glossaryTermsPayload } = buildMappingPayloads();
      await schemaApi.savePageMapping(siteId, mappingPayload, subPagesPayload, pricingPayload, glossaryPayload, glossaryTermsPayload);
      setSaveMsg({ type: 'success', text: 'Mappings saved.' });
      const updated = await schemaApi.getPageMapping(siteId);
      setPageSchemaRows(updated.mapping);
      setSubPageMappings(updated.subPageMappings ?? []);
      setPricingMappings(updated.pricingMappings ?? []);
      setGlossaryMappings(updated.glossaryMappings ?? []);
      setGlossaryTermMappings(updated.glossaryTermMappings ?? []);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const detail = axiosErr?.response?.data?.error ?? axiosErr?.message ?? 'Unknown error';
      setSaveMsg({ type: 'error', text: `Save failed: ${detail}` });
    } finally {
      setSavingOnly(false);
    }
  };

  // ── Generate & inject schemas (reads from already-saved DB mappings) ──────
  const handleGenerateSchemas = async () => {
    setSubmitting(true);
    setMessage(null);
    setProgress('Submitting to schema server…');
    let startedAsync = false;
    try {

      const result = await schemaApi.submitPagesToBka(siteId);

      // Async path (202) — background Lambda is running, poll for completion
      if (result.jobId) {
        startedAsync = true;
        const jobId     = result.jobId;
        const startedAt = Date.now();
        const TIMEOUT   = 5 * 60 * 1000;

        setProgress('Injecting page schemas — this may take up to a minute…');

        const pagesPollInterval = setInterval(async () => {
          try {
            if (Date.now() - startedAt > TIMEOUT) {
              clearInterval(pagesPollInterval);
              setSubmitting(false);
              setProgress(null);
              setMessage({ type: 'error', text: 'Page schema submission is taking longer than expected. Check page statuses and try again.' });
              return;
            }

            const job = await schemaApi.getJobStatus(jobId, siteId);

            if (job.status === 'done') {
              clearInterval(pagesPollInterval);
              setSubmitting(false);
              setProgress(null);
              const r = job.result;
              const injectionNote = !r?.schemaOutputEnabled
                ? ' Schema Output is disabled — turn it on in Entity Settings to inject schemas into your pages.'
                : '';
              const warningLines = (r?.warnings?.length ?? 0) > 0
                ? '\nWarnings:\n' + r!.warnings!.map((w: string) => `• ${w}`).join('\n')
                : '';
              const injected = r?.injected ?? 0;
              setMessage({
                type: (r?.warnings?.length ?? 0) > 0 ? 'warning' : 'success',
                text: `Done — ${injected} page${injected !== 1 ? 's' : ''} injected, ${r?.failed ?? 0} failed.${injectionNote}${warningLines}`,
              });
              // Reload mapping to show updated injection statuses
              const updated = await schemaApi.getPageMapping(siteId);
              setPageSchemaRows(updated.mapping);
              setSubPageMappings(updated.subPageMappings ?? []);
              setPricingMappings(updated.pricingMappings ?? []);
              setGlossaryMappings(updated.glossaryMappings ?? []);
              setGlossaryTermMappings(updated.glossaryTermMappings ?? []);
            } else if (job.status === 'failed') {
              clearInterval(pagesPollInterval);
              setSubmitting(false);
              setProgress(null);
              setMessage({ type: 'error', text: `Submit failed: ${job.error ?? 'Unknown error'}` });
            }
          } catch {
            // Network hiccup — keep polling
          }
        }, 3000);
        return; // setSubmitting(false) handled by poll callbacks
      }

      // Synchronous fallback (should not occur, but handle defensively)
      setProgress(null);
      const injectionNote = !result.schemaOutputEnabled
        ? ' Schema Output is disabled — turn it on in Entity Settings to inject schemas into your pages.'
        : '';
      const warningLines = (result.warnings?.length ?? 0) > 0
        ? '\nWarnings:\n' + result.warnings!.map((w: string) => `• ${w}`).join('\n')
        : '';
      const injected = result.injected ?? 0;
      setMessage({
        type: (result.warnings?.length ?? 0) > 0 ? 'warning' : 'success',
        text: `Done — ${injected} page${injected !== 1 ? 's' : ''} injected, ${result.failed ?? 0} failed.${injectionNote}${warningLines}`,
      });
      const updated = await schemaApi.getPageMapping(siteId);
      setPageSchemaRows(updated.mapping);
      setSubPageMappings(updated.subPageMappings ?? []);
      setPricingMappings(updated.pricingMappings ?? []);
      setGlossaryMappings(updated.glossaryMappings ?? []);
      setGlossaryTermMappings(updated.glossaryTermMappings ?? []);
    } catch (err: unknown) {
      setProgress(null);
      const axiosErr = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
      const serverMsg = axiosErr?.response?.data?.error;
      const status    = axiosErr?.response?.status;
      const detail    = serverMsg ? `${serverMsg}` : axiosErr?.message ?? 'Unknown error';
      console.error('[SaveMapping] failed:', status, detail, err);
      setMessage({ type: 'error', text: `Submit failed (${status ?? 'network error'}): ${detail}` });
    } finally {
      if (!startedAsync) setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>;
  }

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Page list header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold">Map Page Types to Your Site Pages</h3>
          <p className="text-sm text-white/60 mt-0.5">
            Identify which HubSpot page corresponds to each semantic page type.
          </p>
        </div>
        <button
          onClick={() => loadPagesList(true)}
          disabled={pagesLoading}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:opacity-50 transition-colors font-medium"
          title="Refresh page list from HubSpot (clears cache)"
        >
          <RefreshCw className={`w-4 h-4 ${pagesLoading ? 'animate-spin' : ''}`} />
          {pagesLoading ? 'Refreshing…' : 'Refresh Page List'}
        </button>
      </div>

      {cachedAt && (
        <p className="text-xs text-white/40">
          Page list cached: {new Date(cachedAt).toLocaleString()} —{' '}
          {cmsPages.filter(p => !p.isBlogListing).length} site page(s),{' '}
          {cmsPages.filter(p => p.isBlogListing).length} blog listing page(s)
        </p>
      )}

      {/* Mapping rows */}
      <div className="space-y-3">
        {PAGE_TYPES.map(pt => {
          const row    = getRowStatus(pt.key);
          const state  = mapping[pt.key] ?? emptyMappingState();
          const pageId = state.cmsPageId;

          const updateState = (patch: Partial<PageMappingState>) => {
            setMapping(prev => ({ ...prev, [pt.key]: { ...(prev[pt.key] ?? emptyMappingState()), ...patch } }));
          };

          return (
            <div key={pt.key} className="bg-[var(--bg-secondary)] rounded-xl p-4 border border-[var(--bg-tertiary)]">
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0 space-y-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{pt.label}</span>
                      <span className="text-xs text-white/40">{pt.description}</span>
                      {row && statusBadge(row.injectionStatus)}
                    </div>
                    <PageSearchCombobox
                      pages={cmsPages.filter(p => !p.isBlogListing)}
                      value={pageId}
                      onChange={page => {
                        updateState({ cmsPageId: page?.id ?? null });
                        setPageSchemaRows(prev => prev.map(r =>
                          r.pageType === pt.key
                            ? { ...r, injectionStatus: 'pending', injectionError: null }
                            : r
                        ));
                      }}
                    />
                    {row?.injectionError && row.injectionStatus === 'failed' && (
                      <p className="text-xs text-red-400 mt-1">Last attempt: {row.injectionError}</p>
                    )}
                  </div>

                  {/* How is this page structured + inline sub-page mapping */}
                  {pt.hasSubtype && pageId && (() => {
                    const singularLower = pt.label.replace(/s$/, '').toLowerCase();

                    return (
                      <div className="mt-1 rounded-xl border border-white/10 overflow-hidden">
                        {/* Radio question */}
                        <div className="px-4 py-3 bg-white/[0.02]">
                          <p className="text-xs font-medium text-white/70 mb-2">
                            How are your {pt.label.toLowerCase()} presented on this page?
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3">
                            {[
                              { value: 'standalone', label: `All ${pt.label.toLowerCase()} are described on this page`,      hint: 'One page covers everything — generates CollectionPage schema' },
                              { value: 'listing',    label: `This page links to individual ${singularLower} pages`, hint: 'Overview page linking out — generates CollectionPage + sub-page links' },
                            ].map(opt => (
                              <label key={opt.value} className="flex items-start gap-2 cursor-pointer group flex-1">
                                <input
                                  type="radio"
                                  name={`subtype-${pt.key}`}
                                  value={opt.value}
                                  checked={state.subtype === opt.value}
                                  onChange={() => updateState({ subtype: opt.value })}
                                  className="mt-0.5 accent-[#ffdc18] flex-shrink-0"
                                />
                                <span>
                                  <span className="text-sm font-medium group-hover:text-white/90 block">{opt.label}</span>
                                  <span className="text-xs text-white/40">{opt.hint}</span>
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        {/* Individual sub-page mapping — always shown when a page is mapped */}
                        {(() => {
                          const knownEntityIds = new Set(
                            allProducts.map(e => e.entityId || slugify(e.name))
                          );
                          // Custom rows = saved mappings whose entity isn't in Entity Settings
                          const customRows = subPageMappings.filter(
                            sp => sp.linkedEntityId && !knownEntityIds.has(sp.linkedEntityId)
                          );

                          const schemaTypeBadge = (schemaType: string) => {
                            const label = (() => {
                              switch (schemaType) {
                                case 'SoftwareApplication': return 'SAAS / APP';
                                case 'ProfessionalService':
                                case 'Service':            return 'SERVICE';
                                case 'Course':             return 'COURSE';
                                case 'Product':            return 'PRODUCT';
                                case 'LocalBusiness':      return 'BUSINESS';
                                default:                   return schemaType?.toUpperCase() ?? 'ENTITY';
                              }
                            })();
                            const color = (() => {
                              switch (schemaType) {
                                case 'SoftwareApplication': return 'bg-emerald-500/20 text-emerald-300';
                                case 'ProfessionalService':
                                case 'Service':             return 'bg-blue-500/20 text-blue-300';
                                case 'Course':              return 'bg-purple-500/20 text-purple-300';
                                case 'Product':             return 'bg-orange-500/20 text-orange-300';
                                default:                    return 'bg-white/10 text-white/60';
                              }
                            })();
                            return <span className={`flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${color}`}>{label}</span>;
                          };

                          return (
                            <div className="border-t border-white/10">
                              <div className="flex items-center justify-between px-4 py-3 bg-white/[0.02]">
                                <div>
                                  <p className="text-xs font-semibold text-white/80">
                                    Individual {pt.label} Pages
                                  </p>
                                  <p className="text-xs text-white/40 mt-0.5">
                                    If each item has its own dedicated page, map it here. The server will generate richer individual-page schema for each one.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setSubPageMappings(prev => [
                                    ...prev,
                                    { pageType: 'service_page', linkedEntityId: `custom-${Date.now()}`,
                                      entityName: '', cmsPageId: null, isCustom: true } as SubPageMappingEntry & { isCustom: boolean },
                                  ])}
                                  className="flex-shrink-0 ml-4 flex items-center gap-1 text-xs px-2.5 py-1.5 border border-white/15 rounded-lg hover:bg-white/5 transition-colors text-white/50 hover:text-white/80"
                                  title="Add a page whose entity isn't in Entity Settings yet"
                                >
                                  <Plus className="w-3 h-3" /> Add manually
                                </button>
                              </div>

                              {allProducts.length === 0 && customRows.length === 0 ? (
                                <p className="px-4 py-3 text-xs text-white/30 italic">
                                  No products or services in Entity Settings yet.{' '}
                                  Add them there first, or click &ldquo;Add manually&rdquo; to map a page now.
                                </p>
                              ) : (
                                <div className="divide-y divide-white/5">
                                  {/* Auto-populated rows — one per entity in settings */}
                                  {allProducts.map(entity => {
                                    const entityId      = entity.entityId || slugify(entity.name);
                                    const existing      = subPageMappings.find(sp => sp.linkedEntityId === entityId);
                                    const isLinked      = !!existing?.cmsPageId;
                                    const entityPageType: 'product_page' | 'service_page' =
                                      isServiceEntity(entity) ? 'service_page' : 'product_page';

                                    return (
                                      <div key={entityId} className="flex items-center gap-3 px-4 py-3">
                                        <div className="flex-1 min-w-0 flex items-center gap-2">
                                          {schemaTypeBadge(entity.schemaType)}
                                          <div className="min-w-0">
                                            <span className="text-sm font-medium">{entity.name}</span>
                                            {entity.url && (
                                              <span className="ml-2 text-xs text-white/30 truncate">
                                                {entity.url.replace(/^https?:\/\/[^/]+/, '')}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="w-56 flex-shrink-0">
                                          <PageSearchCombobox
                                            pages={cmsPages.filter(p => !p.isBlogListing)}
                                            value={existing?.cmsPageId ?? null}
                                            placeholder="Select page on your site…"
                                            onChange={page => {
                                              setSubPageMappings(prev => {
                                                const idx = prev.findIndex(sp => sp.linkedEntityId === entityId);
                                                const entry: SubPageMappingEntry = {
                                                  pageType:       entityPageType,
                                                  linkedEntityId: entityId,
                                                  entityName:     entity.name,
                                                  cmsPageId:   page?.id   ?? null,
                                                  cmsPageUrl:  page?.url  ?? null,
                                                  cmsPageName: page?.name ?? null,
                                                };
                                                if (idx >= 0) {
                                                  const next = [...prev]; next[idx] = entry; return next;
                                                }
                                                return [...prev, entry];
                                              });
                                            }}
                                          />
                                        </div>
                                        <div className="flex-shrink-0 w-20 text-right">
                                          {isLinked ? (
                                            <div className="space-y-0.5">
                                              <span className="text-xs text-green-400 font-medium flex items-center justify-end gap-1">
                                                ✓ Linked
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() => setSubPageMappings(prev =>
                                                  prev.map(sp => sp.linkedEntityId === entityId
                                                    ? { ...sp, cmsPageId: null, cmsPageUrl: null, cmsPageName: null }
                                                    : sp
                                                  )
                                                )}
                                                className="text-[11px] text-white/30 hover:text-red-400 transition-colors"
                                              >
                                                Unlink
                                              </button>
                                            </div>
                                          ) : (
                                            <span className="text-xs text-white/25">Not linked</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}

                                  {/* Manual / custom rows — entity not in Entity Settings */}
                                  {customRows.map((row) => {
                                    const globalIdx = subPageMappings.findIndex(sp => sp.linkedEntityId === row.linkedEntityId);
                                    return (
                                      <div key={row.linkedEntityId} className="flex items-center gap-3 px-4 py-3 bg-white/[0.015]">
                                        {/* Custom entity name input */}
                                        <div className="flex-1 min-w-0 space-y-1">
                                          <input
                                            value={row.entityName}
                                            onChange={e => {
                                              const name = e.target.value;
                                              setSubPageMappings(prev => prev.map((sp, idx) =>
                                                idx === globalIdx
                                                  ? { ...sp, entityName: name, linkedEntityId: slugify(name) || sp.linkedEntityId }
                                                  : sp
                                              ));
                                            }}
                                            placeholder="Entity name (e.g. My New Service)"
                                            className={`${inputCls} text-sm`}
                                          />
                                          {row.entityName && (
                                            <p className="text-[10px] text-white/30 font-mono">
                                              ID: {slugify(row.entityName) || '…'}
                                            </p>
                                          )}
                                        </div>
                                        {/* Page picker — also auto-fills entity name from page title */}
                                        <div className="w-56 flex-shrink-0">
                                          <PageSearchCombobox
                                            pages={cmsPages.filter(p => !p.isBlogListing)}
                                            value={row.cmsPageId}
                                            placeholder="Select page on your site…"
                                            onChange={page => setSubPageMappings(prev =>
                                              prev.map((sp, idx) => {
                                                if (idx !== globalIdx) return sp;
                                                const autoName = !sp.entityName && page?.name ? page.name : sp.entityName;
                                                return {
                                                  ...sp,
                                                  cmsPageId:      page?.id   ?? null,
                                                  cmsPageUrl:     page?.url  ?? null,
                                                  cmsPageName:    page?.name ?? null,
                                                  entityName:     autoName,
                                                  linkedEntityId: autoName
                                                    ? slugify(autoName) || sp.linkedEntityId
                                                    : sp.linkedEntityId,
                                                };
                                              })
                                            )}
                                          />
                                        </div>
                                        {/* Remove */}
                                        <div className="flex-shrink-0 w-20 flex items-center justify-end">
                                          <button
                                            type="button"
                                            onClick={() => setSubPageMappings(prev =>
                                              prev.filter((_, idx) => idx !== globalIdx)
                                            )}
                                            className="p-1 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                                            title="Remove this row"
                                          >
                                            <X className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}

                  {/* Contact Points editor for contact page */}
                  {pt.hasContactPoints && pageId && (
                    <ContactPointsEditor
                      points={state.contactPoints}
                      onChange={pts => updateState({ contactPoints: pts })}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Pricing Pages section ──────────────────────────────────────────── */}
      <div className="bg-[var(--bg-secondary)] rounded-xl p-4 border border-[var(--bg-tertiary)] space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium text-sm">Pricing Pages</span>
            <span className="ml-2 text-xs text-white/40">One or more CMS pricing pages</span>
            {offersWithoutLinkedIds > 0 && productCount > 1 && (
              <p className="mt-1 text-xs text-amber-400/80 flex items-start gap-1.5">
                <span className="mt-0.5">⚠</span>
                <span>
                  {offersWithoutLinkedIds} offer{offersWithoutLinkedIds !== 1 ? 's' : ''} lack an "Applies To" link.
                  The pricing schema <em>about</em> field may be incomplete.
                </span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setPricingMappings(prev => [...prev, { cmsPageId: null }])}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Plus className="w-3 h-3" /> Add Pricing Page
          </button>
        </div>

        {pricingMappings.length === 0 ? (
          <p className="text-xs text-white/30 italic">No pricing pages mapped. Click "Add Pricing Page" to map one.</p>
        ) : (
          <div className="space-y-2">
            {pricingMappings.map((pp, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="flex-1">
                  <PageSearchCombobox
                    pages={cmsPages.filter(p => !p.isBlogListing)}
                    value={pp.cmsPageId}
                    onChange={page => {
                      setPricingMappings(prev => {
                        const next = [...prev];
                        next[idx] = {
                          ...next[idx],
                          cmsPageId:   page?.id   ?? null,
                          cmsPageUrl:  page?.url  ?? null,
                          cmsPageName: page?.name ?? null,
                        };
                        return next;
                      });
                    }}
                  />
                </div>
                {pp.injectionStatus && (
                  <div className="flex-shrink-0">{statusBadge(pp.injectionStatus)}</div>
                )}
                {pp.injectionError && pp.injectionStatus === 'failed' && (
                  <p className="text-xs text-red-400 flex-shrink-0">{pp.injectionError}</p>
                )}
                <button
                  type="button"
                  onClick={() => setPricingMappings(prev => prev.filter((_, j) => j !== idx))}
                  className="flex-shrink-0 p-1 text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                  title="Remove this pricing page"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Glossary Pages section ──────────────────────────────────────────── */}
      <div className="bg-[var(--bg-secondary)] rounded-xl p-4 border border-[var(--bg-tertiary)] space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium text-sm">Glossary Pages</span>
            <span className="ml-2 text-xs text-white/40">One or more glossary pages with optional term sub-pages</span>
          </div>
          <button
            type="button"
            onClick={() => setGlossaryMappings(prev => [
              ...prev,
              {
                linkedEntityId:      null,
                cmsPageId:           null,
                hasGlossarySubpages: false,
                description:         null,
              } as GlossaryMappingEntry,
            ])}
            className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            <Plus className="w-3 h-3" /> Add Glossary
          </button>
        </div>

        {glossaryMappings.length === 0 ? (
          <p className="text-xs text-white/30 italic">
            No glossary pages mapped. Click &ldquo;Add Glossary&rdquo; to map one.
          </p>
        ) : (
          <div className="space-y-5">
            {glossaryMappings.map((gr, grIdx) => {
              // Terms that belong to this glossary root (match by root URL)
              const rootUrl = gr.cmsPageUrl ?? (gr.cmsPageId ? cmsPages.find(p => p.id === gr.cmsPageId)?.url : null) ?? null;
              const myTerms = glossaryTermMappings.filter(gt => gt.parentGlossaryUrl === rootUrl);

              return (
                <div key={grIdx} className="border border-white/10 rounded-lg p-3 space-y-3">
                  {/* Header row */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-white/70">
                      Glossary #{grIdx + 1}
                      {gr.linkedEntityId ? <span className="ml-1.5 text-white/40">({gr.linkedEntityId})</span> : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        // Remove this root and all its terms
                        const urlToRemove = rootUrl;
                        setGlossaryMappings(prev => prev.filter((_, j) => j !== grIdx));
                        if (urlToRemove) {
                          setGlossaryTermMappings(prev => prev.filter(gt => gt.parentGlossaryUrl !== urlToRemove));
                        }
                      }}
                      className="p-1 text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                      title="Remove this glossary"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* CMS page picker */}
                  <div className="space-y-1">
                    <label className="text-xs text-white/50">CMS Page</label>
                    <PageSearchCombobox
                      pages={cmsPages.filter(p => !p.isBlogListing)}
                      value={gr.cmsPageId}
                      onChange={page => {
                        setGlossaryMappings(prev => {
                          const next = [...prev];
                          const oldUrl = next[grIdx].cmsPageUrl ?? null;
                          next[grIdx] = {
                            ...next[grIdx],
                            cmsPageId:   page?.id   ?? null,
                            cmsPageUrl:  page?.url  ?? null,
                            cmsPageName: page?.name ?? null,
                          };
                          // Repoint any orphaned terms to the new URL
                          if (oldUrl && page?.url && oldUrl !== page.url) {
                            setGlossaryTermMappings(terms => terms.map(gt =>
                              gt.parentGlossaryUrl === oldUrl
                                ? { ...gt, parentGlossaryUrl: page.url, parentGlossaryName: page.name ?? gt.parentGlossaryName }
                                : gt
                            ));
                          }
                          return next;
                        });
                      }}
                    />
                  </div>

                  {/* Stable ID (required when >1 glossary or Mode B) */}
                  <div className="space-y-1">
                    <label className="text-xs text-white/50">
                      Glossary ID{glossaryMappings.length > 1 || gr.hasGlossarySubpages ? <span className="text-red-400 ml-0.5">*</span> : null}
                      <span className="ml-1 text-white/30">(stable URL-safe slug, e.g. &ldquo;msp-decoder&rdquo;)</span>
                    </label>
                    <input
                      type="text"
                      className={inputCls}
                      placeholder="e.g. msp-decoder"
                      value={gr.linkedEntityId ?? ''}
                      onChange={e => setGlossaryMappings(prev => {
                        const next = [...prev];
                        next[grIdx] = { ...next[grIdx], linkedEntityId: e.target.value || null };
                        return next;
                      })}
                    />
                    {(glossaryMappings.length > 1 || gr.hasGlossarySubpages) && !gr.linkedEntityId && (
                      <p className="text-xs text-amber-400/80">Required when you have multiple glossaries or use Mode B (sub-pages).</p>
                    )}
                  </div>

                  {/* Mode selector */}
                  <div className="space-y-1">
                    <label className="text-xs text-white/50">Glossary Mode</label>
                    <div className="flex gap-3">
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name={`glossary-mode-${grIdx}`}
                          checked={!gr.hasGlossarySubpages}
                          onChange={() => setGlossaryMappings(prev => {
                            const next = [...prev];
                            next[grIdx] = { ...next[grIdx], hasGlossarySubpages: false };
                            return next;
                          })}
                        />
                        <span>Mode A — AI extracts terms from page HTML</span>
                      </label>
                      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                          type="radio"
                          name={`glossary-mode-${grIdx}`}
                          checked={gr.hasGlossarySubpages}
                          onChange={() => setGlossaryMappings(prev => {
                            const next = [...prev];
                            next[grIdx] = { ...next[grIdx], hasGlossarySubpages: true };
                            return next;
                          })}
                        />
                        <span>Mode B — each term has its own page</span>
                      </label>
                    </div>
                  </div>

                  {/* Description (required in Mode B) */}
                  {gr.hasGlossarySubpages && (
                    <div className="space-y-1">
                      <label className="text-xs text-white/50">
                        Glossary Description<span className="text-red-400 ml-0.5">*</span>
                        <span className="ml-1 text-white/30">(vocabulary scope — used as DefinedTermSet.description)</span>
                      </label>
                      <textarea
                        rows={2}
                        className={inputCls + ' resize-none'}
                        placeholder="e.g. The structured lexicon of proprietary terminology covering…"
                        value={gr.description ?? ''}
                        onChange={e => setGlossaryMappings(prev => {
                          const next = [...prev];
                          next[grIdx] = { ...next[grIdx], description: e.target.value || null };
                          return next;
                        })}
                      />
                      {!gr.description && (
                        <p className="text-xs text-amber-400/80">Description is required in Mode B.</p>
                      )}
                    </div>
                  )}

                  {/* Injection status badge */}
                  {gr.injectionStatus && (
                    <div className="flex items-center gap-2">
                      {statusBadge(gr.injectionStatus)}
                      {gr.injectionError && gr.injectionStatus === 'failed' && (
                        <span className="text-xs text-red-400">{gr.injectionError}</span>
                      )}
                    </div>
                  )}

                  {/* Mode B: term sub-pages */}
                  {gr.hasGlossarySubpages && (
                    <div className="mt-2 space-y-3 pl-3 border-l border-white/10">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-white/50">Term Pages</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            title="Detect CMS pages whose URL starts with this glossary's URL"
                            onClick={() => {
                              const parentUrl = gr.cmsPageUrl
                                ?? (gr.cmsPageId ? cmsPages.find(p => p.id === gr.cmsPageId)?.url : null)
                                ?? null;
                              if (!parentUrl) return;
                              // Strip trailing slash from parent URL for prefix matching
                              const prefix = parentUrl.replace(/\/$/, '');
                              const childPages = cmsPages.filter(p => {
                                const u = p.url?.replace(/\/$/, '') ?? '';
                                return u !== prefix && u.startsWith(prefix + '/');
                              });
                              const parentGlossaryName = gr.cmsPageName
                                ?? (gr.cmsPageId ? cmsPages.find(pg => pg.id === gr.cmsPageId)?.name : null)
                                ?? null;
                              const newTerms: GlossaryTermMappingEntry[] = childPages.map(cp => ({
                                linkedEntityId:     slugify(cp.url?.replace(prefix + '/', '').replace(/\/$/, '') ?? ''),
                                cmsPageId:          cp.id,
                                cmsPageUrl:         cp.url ?? null,
                                cmsPageName:        cp.name,
                                parentGlossaryUrl:  parentUrl,
                                parentGlossaryName: parentGlossaryName,
                                termCode:           null,
                                description:        null,
                              }));
                              if (newTerms.length === 0) return;
                              // Merge: skip if cmsPageId already present
                              const existingIds = new Set(glossaryTermMappings.map(t => t.cmsPageId));
                              const toAdd = newTerms.filter(t => !existingIds.has(t.cmsPageId));
                              if (toAdd.length > 0) setGlossaryTermMappings(prev => [...prev, ...toAdd]);
                            }}
                            className="flex items-center gap-1 text-xs px-2 py-1 border border-white/15 rounded-lg hover:bg-white/5 transition-colors text-white/60 hover:text-white"
                          >
                            <Search className="w-3 h-3" /> Auto-detect term sub-pages from child pages
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              const currentRootUrl  = gr.cmsPageUrl ?? (gr.cmsPageId ? cmsPages.find(p => p.id === gr.cmsPageId)?.url : null) ?? null;
                              const currentRootName = gr.cmsPageName ?? (gr.cmsPageId ? cmsPages.find(p => p.id === gr.cmsPageId)?.name : null) ?? null;
                              setGlossaryTermMappings(prev => [
                                ...prev,
                                {
                                  linkedEntityId:     '',
                                  cmsPageId:          null,
                                  parentGlossaryUrl:  currentRootUrl,
                                  parentGlossaryName: currentRootName,
                                  termCode:           null,
                                  description:        null,
                                } as GlossaryTermMappingEntry,
                              ]);
                            }}
                            className="flex items-center gap-1 text-xs px-2 py-1 border border-white/15 rounded-lg hover:bg-white/5 transition-colors"
                          >
                            <Plus className="w-3 h-3" /> Add term page
                          </button>
                        </div>
                      </div>

                      {myTerms.length === 0 ? (
                        <p className="text-xs text-white/30 italic">No term sub-pages yet.</p>
                      ) : (
                        <div className="space-y-3">
                          {myTerms.map((gt) => {
                            const gtIdx = glossaryTermMappings.findIndex(t => t === gt);
                            return (
                              <div key={gtIdx} className="border border-white/8 rounded-md p-2.5 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-white/40">Term</span>
                                  <button
                                    type="button"
                                    onClick={() => setGlossaryTermMappings(prev => prev.filter((_, j) => j !== gtIdx))}
                                    className="p-0.5 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                                    title="Remove term"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>

                                {/* Term CMS page */}
                                <div className="space-y-1">
                                  <label className="text-xs text-white/40">Term Page</label>
                                  <PageSearchCombobox
                                    pages={cmsPages.filter(p => !p.isBlogListing)}
                                    value={gt.cmsPageId}
                                    onChange={page => setGlossaryTermMappings(prev => {
                                      const next = [...prev];
                                      next[gtIdx] = {
                                        ...next[gtIdx],
                                        cmsPageId:   page?.id   ?? null,
                                        cmsPageUrl:  page?.url  ?? null,
                                        cmsPageName: page?.name ?? null,
                                      };
                                      return next;
                                    })}
                                  />
                                </div>

                                {/* Term linked_entity_id */}
                                <div className="space-y-1">
                                  <label className="text-xs text-white/40">
                                    Term ID (slug)<span className="text-red-400 ml-0.5">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    className={inputCls}
                                    placeholder="e.g. /signals/symmetry-gate"
                                    value={gt.linkedEntityId}
                                    onChange={e => setGlossaryTermMappings(prev => {
                                      const next = [...prev];
                                      next[gtIdx] = { ...next[gtIdx], linkedEntityId: e.target.value };
                                      return next;
                                    })}
                                  />
                                </div>

                                {/* Term code (optional) */}
                                <div className="space-y-1">
                                  <label className="text-xs text-white/40">
                                    Term code <span className="text-white/25">(optional)</span>
                                  </label>
                                  <input
                                    type="text"
                                    className={inputCls}
                                    placeholder="e.g. SFKU (optional)"
                                    value={gt.termCode ?? ''}
                                    onChange={e => setGlossaryTermMappings(prev => {
                                      const next = [...prev];
                                      next[gtIdx] = { ...next[gtIdx], termCode: e.target.value || null };
                                      return next;
                                    })}
                                  />
                                </div>

                                {/* Term description */}
                                <div className="space-y-1">
                                  <label className="text-xs text-white/40">
                                    Definition<span className="text-red-400 ml-0.5">*</span>
                                  </label>
                                  <textarea
                                    rows={2}
                                    className={inputCls + ' resize-none'}
                                    placeholder="The formal definition of this term…"
                                    value={gt.description ?? ''}
                                    onChange={e => setGlossaryTermMappings(prev => {
                                      const next = [...prev];
                                      next[gtIdx] = { ...next[gtIdx], description: e.target.value || null };
                                      return next;
                                    })}
                                  />
                                </div>

                                {/* Term injection status */}
                                {gt.injectionStatus && (
                                  <div className="flex items-center gap-2">
                                    {statusBadge(gt.injectionStatus)}
                                    {gt.injectionError && gt.injectionStatus === 'failed' && (
                                      <span className="text-xs text-red-400">{gt.injectionError}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Action bar — Step 1: Save, Step 2: Generate */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Step 1 */}
          <button
            onClick={handleSaveMappings}
            disabled={savingOnly || submitting}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm border border-white/20"
          >
            {savingOnly
              ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</>
              : <><CheckCircle2 className="w-4 h-4" />Save Mappings</>
            }
          </button>

          {/* Step 2 */}
          <button
            onClick={handleGenerateSchemas}
            disabled={submitting || savingOnly}
            className={btnPrimary}
          >
            {submitting
              ? <><Loader2 className="w-4 h-4 animate-spin" />{progress || 'Working…'}</>
              : <><Globe className="w-4 h-4" />Generate &amp; Inject Schemas</>
            }
          </button>
        </div>

        {/* Save-only feedback */}
        {saveMsg && (
          <p className={`text-sm ${saveMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
            {saveMsg.text}
          </p>
        )}

        {/* Generate feedback */}
        {message && (
          <span className={`text-sm whitespace-pre-wrap ${
            message.type === 'success' ? 'text-green-400' :
            message.type === 'warning' ? 'text-yellow-400' :
            message.type === 'info'    ? 'text-blue-400' :
            'text-red-400'
          }`}>
            {message.text}
          </span>
        )}
      </div>

      <p className="text-xs text-white/30">
        <strong className="text-white/50">Step 1 — Save Mappings:</strong> stores your page selections in the database.
        Do this as many times as needed until all pages and sub-pages are configured.{' '}
        <strong className="text-white/50">Step 2 — Generate &amp; Inject Schemas:</strong> sends page content to the schema server,
        generates JSON-LD for every saved mapping, and injects it into each page&apos;s &lt;head&gt;.
      </p>

      {/* ── Heading Structure Analysis ───────────────────────────────────── */}
      <div className="pt-4 border-t border-[var(--bg-tertiary)]">
        <div className="mb-3">
          <h3 className="font-semibold text-sm flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-white/60" />
            Heading Structure Analysis
          </h3>
          <p className="text-xs text-white/50 mt-1">
            Analyze H2/H3/H4 ratio and get actionable heading recommendations for each mapped page.
            Click "Analyze" to run, then use the modal to review changes.
            Results are informational only — no changes are applied automatically to pages.
          </p>
        </div>

        {headingMsg && (
          <p className={`text-sm mb-3 ${headingMsg.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
            {headingMsg.text}
          </p>
        )}

        <div className="space-y-2">
          {/* Standard page types */}
          {PAGE_TYPES.filter(pt => mapping[pt.key]?.cmsPageId).map(pt => {
            const analyzeKey  = pt.key;
            const analysis    = headingAnalyses.find(a => a.pageType === pt.key && !a.linkedEntityId);
            const isAnalyzing = analyzingPage === analyzeKey;
            const pendingCount = (Array.isArray(analysis?.analysisJson?.recommendations)
              ? (analysis!.analysisJson!.recommendations as unknown[]).length
              : 0);
            const pageId = mapping[pt.key]?.cmsPageId ?? null;

            return (
              <div key={pt.key} className="bg-[var(--bg-secondary)] rounded-xl px-4 py-3 border border-[var(--bg-tertiary)] flex flex-wrap items-center gap-3">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-white/10 text-white/70 uppercase tracking-wide">
                  {pt.label.replace(/\s+page$/i, '')}
                </span>
                <span className="text-sm font-medium flex-1 min-w-0 truncate">
                  {cmsPages.find(p => p.id === pageId)?.name ?? pageId}
                </span>

                {analysis && (
                  <>
                    {pendingCount > 0 && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-400/20 text-amber-300">
                        {pendingCount} pending
                      </span>
                    )}
                    {analysis.score != null && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#ffdc18]/20 text-[#ffdc18]">
                        {analysis.score}/100
                      </span>
                    )}
                    {analysis.analysisDate && (
                      <span className="text-xs text-white/30">
                        {new Date(analysis.analysisDate).toLocaleDateString()}
                      </span>
                    )}
                    <button
                      onClick={() => setViewingAnalysis(analysis)}
                      className="px-3 py-1 text-xs border border-white/20 rounded hover:bg-white/10 transition-colors"
                    >
                      View Results
                    </button>
                  </>
                )}

                <button
                  onClick={() => handleAnalyzePage(pt.key)}
                  disabled={isAnalyzing || analyzingPage !== null}
                  className="px-3 py-1 text-xs border border-white/20 rounded hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  {isAnalyzing
                    ? <><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Analyzing…</>
                    : analysis ? 'Re-analyze' : 'Analyze'
                  }
                </button>
                {isAnalyzing && (
                  <span className="text-xs text-white/40 italic">This may take up to 2 minutes — please keep this tab open.</span>
                )}
              </div>
            );
          })}

          {/* Sub-pages (product_page / service_page) with entityId */}
          {subPageMappings.filter(sp => sp.cmsPageId).map(sp => {
            const analyzeKey  = `${sp.pageType}/${sp.linkedEntityId}`;
            const analysis    = headingAnalyses.find(
              a => a.pageType === sp.pageType && a.linkedEntityId === sp.linkedEntityId
            );
            const isAnalyzing = analyzingPage === analyzeKey;
            const pendingCount = (Array.isArray(analysis?.analysisJson?.recommendations)
              ? (analysis!.analysisJson!.recommendations as unknown[]).length
              : 0);
            const typeLabel = sp.pageType === 'service_page' ? 'Service Page' : 'Product Page';
            const pageName  = sp.cmsPageName ?? cmsPages.find(p => p.id === sp.cmsPageId)?.name ?? sp.cmsPageId;

            return (
              <div key={analyzeKey} className="bg-[var(--bg-secondary)] rounded-xl px-4 py-3 border border-[var(--bg-tertiary)] flex flex-wrap items-center gap-3">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-300 uppercase tracking-wide">
                  {typeLabel}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block">{sp.entityName || sp.linkedEntityId}</span>
                  {pageName && pageName !== sp.entityName && (
                    <span className="text-xs text-white/35 truncate block">{pageName}</span>
                  )}
                </div>

                {analysis && (
                  <>
                    {pendingCount > 0 && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-400/20 text-amber-300">
                        {pendingCount} pending
                      </span>
                    )}
                    {analysis.score != null && (
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-[#ffdc18]/20 text-[#ffdc18]">
                        {analysis.score}/100
                      </span>
                    )}
                    {analysis.analysisDate && (
                      <span className="text-xs text-white/30">
                        {new Date(analysis.analysisDate).toLocaleDateString()}
                      </span>
                    )}
                    <button
                      onClick={() => setViewingAnalysis(analysis)}
                      className="px-3 py-1 text-xs border border-white/20 rounded hover:bg-white/10 transition-colors"
                    >
                      View Results
                    </button>
                  </>
                )}

                <button
                  onClick={() => handleAnalyzePage(sp.pageType, sp.linkedEntityId)}
                  disabled={isAnalyzing || analyzingPage !== null}
                  className="px-3 py-1 text-xs border border-white/20 rounded hover:bg-white/10 transition-colors disabled:opacity-50"
                >
                  {isAnalyzing
                    ? <><Loader2 className="w-3 h-3 animate-spin inline mr-1" />Analyzing…</>
                    : analysis ? 'Re-analyze' : 'Analyze'
                  }
                </button>
                {isAnalyzing && (
                  <span className="text-xs text-white/40 italic">This may take up to 2 minutes — please keep this tab open.</span>
                )}
              </div>
            );
          })}

          {PAGE_TYPES.filter(pt => mapping[pt.key]?.cmsPageId).length === 0 &&
           subPageMappings.filter(sp => sp.cmsPageId).length === 0 && (
            <p className="text-sm text-white/40 py-2">
              Map pages above to enable heading structure analysis.
            </p>
          )}
        </div>
      </div>

      {/* View Results Modal */}
      {viewingAnalysis && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setViewingAnalysis(null)}>
          <div className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bg-tertiary)]">
              <div>
                <h3 className="font-semibold">Heading Structure Analysis</h3>
                <p className="text-xs text-white/50 mt-0.5">
                  {viewingAnalysis.linkedEntityId
                    ? (() => {
                        const sp = subPageMappings.find(s => s.linkedEntityId === viewingAnalysis.linkedEntityId);
                        return sp?.entityName || viewingAnalysis.linkedEntityId;
                      })()
                    : PAGE_TYPES.find(pt => pt.key === viewingAnalysis.pageType)?.label ?? viewingAnalysis.pageType
                  } ·{' '}
                  {viewingAnalysis.score != null ? `${viewingAnalysis.score}/100` : 'No score'} ·{' '}
                  {viewingAnalysis.analysisDate ? new Date(viewingAnalysis.analysisDate).toLocaleString() : ''}
                </p>
              </div>
              <button onClick={() => setViewingAnalysis(null)} className="p-2 rounded hover:bg-white/10 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-y-auto p-6 space-y-3 flex-1">

              {/* ── Summary card ─────────────────────────────────────────── */}
              {(() => {
                const summary = viewingAnalysis.analysisJson?.analysis_summary as Record<string, unknown> | undefined;
                if (!summary) return null;

                const score       = (summary.overall_score  ?? viewingAnalysis.score)  as number | null;
                const wordCount   = (summary.word_count      ?? summary.wordCount)      as number | undefined;
                const currentH    = (summary.current_heading_count ?? summary.current_headings)   as number | undefined;
                const recommendH  = (summary.recommended_heading_count ?? summary.recommended_headings) as number | undefined;
                const keyIssues   = (summary.key_issues ?? []) as string[];

                type HeadingRec = { action?: string };
                const recs = (viewingAnalysis.analysisJson?.recommendations ?? []) as HeadingRec[];
                const countAction = (a: string) => recs.filter(r => r.action?.toLowerCase() === a).length;
                const keepCt    = countAction('keep');
                const reviseCt  = countAction('revise');
                const elevateCt = countAction('elevate');
                const demoteCt  = countAction('demote');
                const addCt     = countAction('add');

                const scoreBg =
                  score == null         ? 'bg-white/10 text-white/60'
                  : score >= 80         ? 'bg-green-500/20 text-green-400'
                  : score >= 60         ? 'bg-yellow-500/20 text-yellow-400'
                  : score >= 40         ? 'bg-orange-500/20 text-orange-400'
                  :                       'bg-red-500/20 text-red-400';

                return (
                  <div className="rounded-xl bg-[var(--bg-tertiary)] border border-white/10 p-4 space-y-3">
                    {/* Score + stats row */}
                    <div className="flex items-center gap-4 flex-wrap">
                      <span className={`text-3xl font-bold px-3 py-1.5 rounded-lg ${scoreBg}`}>
                        {score != null ? `${score}/100` : '—/100'}
                      </span>
                      {wordCount != null && (
                        <div className="text-center">
                          <p className="text-xs text-white/40 uppercase tracking-wide">Word Count</p>
                          <p className="text-lg font-semibold text-white">{wordCount.toLocaleString()}</p>
                        </div>
                      )}
                      {currentH != null && (
                        <div className="text-center">
                          <p className="text-xs text-white/40 uppercase tracking-wide">Current Headings</p>
                          <p className="text-lg font-semibold text-white">{currentH}</p>
                        </div>
                      )}
                      {recommendH != null && (
                        <div className="text-center">
                          <p className="text-xs text-white/40 uppercase tracking-wide">Recommended</p>
                          <p className="text-lg font-semibold text-white">{recommendH}</p>
                        </div>
                      )}
                    </div>

                    {/* Action pill row */}
                    {recs.length > 0 && (
                      <div className="flex flex-wrap gap-2 text-xs font-medium">
                        {keepCt    > 0 && <span className="px-2 py-0.5 rounded-full bg-green-500/15 text-green-400">✓ {keepCt} keep</span>}
                        {reviseCt  > 0 && <span className="px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400">✏ {reviseCt} revise</span>}
                        {elevateCt > 0 && <span className="px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-400">↑ {elevateCt} elevate</span>}
                        {demoteCt  > 0 && <span className="px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400">↓ {demoteCt} demote</span>}
                        {addCt     > 0 && <span className="px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-400">+ {addCt} add</span>}
                      </div>
                    )}

                    {/* Key issues */}
                    {keyIssues.length > 0 && (
                      <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 space-y-1">
                        <p className="text-xs font-semibold text-red-400 uppercase tracking-wide">Key Issues</p>
                        <ul className="space-y-0.5">
                          {keyIssues.map((issue, i) => (
                            <li key={i} className="text-xs text-red-300/80">• {issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })()}

              <p className="text-xs text-amber-400/80 bg-amber-400/10 rounded-lg px-3 py-2">
                Recommendations are informational only. Apply heading changes manually in HubSpot.
              </p>

              {(() => {
                type HeadingRec = {
                  action?: string;
                  rationale?: string;
                  existing_heading?: { tag?: string; text?: string };
                  recommended?: { tag?: string; text?: string };
                };
                const rawRecs = (viewingAnalysis.analysisJson?.recommendations ?? []) as HeadingRec[];
                if (rawRecs.length === 0) {
                  return <p className="text-sm text-white/40 text-center py-8">No recommendations — heading structure looks good!</p>;
                }

                const actionStyle = (action: string | undefined) => {
                  switch (action?.toLowerCase()) {
                    case 'keep':    return 'bg-green-500/20 text-green-400';
                    case 'revise':  return 'bg-yellow-500/20 text-yellow-400';
                    case 'elevate': return 'bg-blue-500/20 text-blue-400';
                    case 'demote':  return 'bg-orange-500/20 text-orange-400';
                    case 'add':     return 'bg-purple-500/20 text-purple-400';
                    default:        return 'bg-white/10 text-white/60';
                  }
                };

                return rawRecs.map((rec, i) => (
                  <div key={i} className="rounded-lg bg-[var(--bg-tertiary)] px-4 py-3 text-sm space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${actionStyle(rec.action)}`}>
                        {rec.action ?? 'note'}
                      </span>
                      {rec.existing_heading && (
                        <span className="text-white/60">
                          {rec.existing_heading.tag ?? ''}:&nbsp;
                          <span className="text-white">{rec.existing_heading.text ?? ''}</span>
                        </span>
                      )}
                      {rec.recommended && (
                        <>
                          <span className="text-white/30">→</span>
                          <span className="text-green-400">
                            {rec.recommended.tag ?? ''}:&nbsp;{rec.recommended.text ?? ''}
                          </span>
                        </>
                      )}
                    </div>
                    {rec.rationale && <p className="text-xs text-white/50">{rec.rationale}</p>}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 3b: FAQ Section CSS ───────────────────────────────────────────────

const DEFAULT_FAQ_CSS = `/* VizzEx FAQ Section Styles
 * The section inherits your blog post's heading and paragraph styles by default.
 * Add overrides here as needed. Changes take effect on the next injection.
 */

.vzx-faq-section {
  margin-top: 2.5em;
  padding-top: 1.5em;
  border-top: 1px solid currentColor;
  border-color: inherit;
}

/* Override H2 heading styles if needed */
.vzx-faq-section h2 {
}

/* Override H3 question styles if needed */
.vzx-faq-section h3 {
}

/* Override answer paragraph styles if needed */
.vzx-faq-section p {
}`;

function FaqCssTab({ siteId }: { siteId: number }) {
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [cssValue, setCssValue] = useState(DEFAULT_FAQ_CSS);
  const [message,  setMessage]  = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    schemaApi.getGlobalSettings(siteId)
      .then(gs => { if (gs.faqCss != null) setCssValue(gs.faqCss || DEFAULT_FAQ_CSS); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await schemaApi.saveGlobalSettings(siteId, { faqCss: cssValue });
      setMessage({ type: 'success', text: 'FAQ CSS saved. It will be applied on the next injection.' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to save CSS. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>;
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-[var(--bg-secondary)] rounded-xl p-5 border border-[var(--bg-tertiary)]">
        <h3 className="font-semibold mb-1">FAQ Section CSS</h3>
        <p className="text-sm text-white/60 mb-4">
          Custom CSS applied to the FAQ section inserted into each blog post.
          The section uses your blog's existing heading and paragraph styles by default —
          add overrides here as needed.
          Changes take effect on the next analysis or when you manually trigger injection.
        </p>

        <div className="rounded-lg overflow-hidden border border-white/10">
          <CodeMirror
            value={cssValue}
            height="420px"
            theme={vscodeDark}
            extensions={[cssLang()]}
            onChange={setCssValue}
            basicSetup={{ lineNumbers: true, foldGutter: true, autocompletion: true }}
          />
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button onClick={handleSave} disabled={saving} className={`${`flex items-center gap-2 px-4 py-2 bg-[#ffdc18] text-gray-900 font-semibold rounded-lg hover:bg-[#ffdc18]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm`}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {saving ? 'Saving…' : 'Save CSS'}
          </button>
          <button
            onClick={() => setCssValue(DEFAULT_FAQ_CSS)}
            className="px-4 py-2 text-sm border border-white/20 rounded-lg hover:bg-white/5 transition-colors"
          >
            Reset to Defaults
          </button>
          {message && (
            <span className={`text-sm ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
              {message.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Tab 4: FAQ Review ─────────────────────────────────────────────────────
// Full implementation lives in FaqReviewPanel (shared with the standalone
// Post FAQs page used by HTML/no-CMS clients, which has no schema/injection
// controls to show).

function FaqsTab({ siteId }: { siteId: number }) {
  return <FaqReviewPanel siteId={siteId} showSchemaToggle />;
}

// ─── Shared UI components ───────────────────────────────────────────────────
// Section/Field now live in components/schema/SchemaFormControls — reused by
// the standalone Post FAQs page for HTML clients.

